/**
 * Elite Batch 11 Part 5 — Workflow Templates 1028–1047
 * Klaviyo lifecycle, landing pages, launch campaigns, lead forms,
 * lifecycle strategy, link-in-bio, LinkedIn, localized ads, sunset/winback,
 * and loyalty program workflows.
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSmsDetails: { to, message } }, x);
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function bufferNode(
  id: string,
  label: string,
  y: number,
  text: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.bufferapp.com/1/updates/create.json",
        headers: [
          { key: "Authorization", value: "Bearer {buffer_token}" },
          { key: "Content-Type", value: "application/x-www-form-urlencoded" },
        ],
        body: [
          { key: "text", value: text },
          { key: "profile_ids[]", value: "{buffer_profile_id}" },
          { key: "scheduled_at", value: "{scheduled_time}" },
        ],
        variables: [
          { name: "success", customName: "buffer_success", type: "boolean" },
          {
            name: "updates[0].id",
            customName: "buffer_update_id",
            type: "string",
          },
        ],
      },
    },
    x,
  );
}
function hubspotGetNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "GET",
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [],
        variables: vars,
      },
    },
    x,
  );
}
function serpApiNode(
  id: string,
  label: string,
  y: number,
  query: string,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "GET",
        url: `https://serpapi.com/search.json?q=${encodeURIComponent(query)}&api_key={serpapi_key}`,
        headers: [],
        body: [],
        variables: vars,
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 11 PART 5 — TEMPLATES 1028–1047
// =============================================================================

export const ELITE_BATCH11_PART5: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 1028. KLAVIYO SUNSET FLOW (7 nodes)
  // ===================================================================

  "Klaviyo Sunset Flow": {
    summary:
      "Scans for unengaged Klaviyo subscribers on a schedule, validates engagement history, generates an AI re-engagement email, dispatches it via SendGrid, monitors response after a delay, processes sunset decisions in code, and logs audit results to Google Sheets.",
    nodes: [
      trigger("Schedule: Unengaged subscriber scan", "schedule", {
        schedule: "0 8 * * 1 (Monday at 8 AM)",
        howItStarts:
          "A weekly schedule scans Klaviyo for subscribers who have not opened or clicked any email in the past 90 days, initiating the sunset evaluation flow",
        setup:
          "Configure Klaviyo API key with profiles read/write access. Set engagement threshold (e.g., 90 days no open/click). Provide SendGrid API key and Google Sheets spreadsheet ID for audit logging.",
        inputFields: [],
      }),

      // 2. Engagement history validation (runCode)
      n("2", "runCode", "Validate engagement history from Klaviyo", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Fetch unengaged subscribers from Klaviyo and validate engagement history
const response = await fetch('https://a.klaviyo.com/api/profiles/?filter=less-than(properties.last_email_open,2024-01-01T00:00:00Z)&page[size]=100', {
  headers: { 'Authorization': 'Klaviyo-API-Key ' + vars.klaviyo_api_key, 'accept': 'application/json', 'revision': '2024-02-15' }
});
const data = await response.json();
const profiles = (data.data || []);
const unengaged = profiles.map(p => ({
  id: p.id,
  email: p.attributes.email,
  last_open: p.attributes.properties?.last_email_open || 'never',
  last_click: p.attributes.properties?.last_email_click || 'never',
  created: p.attributes.created,
  list_count: p.attributes.properties?.list_count || 0
}));
return {
  unengaged_count: unengaged.length,
  unengaged_profiles_json: JSON.stringify(unengaged),
  scan_timestamp: new Date().toISOString(),
  has_unengaged: unengaged.length > 0 ? "yes" : "no",
  sample_email: unengaged.length > 0 ? unengaged[0].email : ""
};`,
          outputVariables: [
            { name: "unengaged_count", type: "number" },
            { name: "unengaged_profiles_json", type: "string" },
            { name: "scan_timestamp", type: "string" },
            { name: "has_unengaged", type: "string" },
            { name: "sample_email", type: "string" },
          ],
        },
      }),

      // 3. AI re-engagement email generation
      aiNode(
        "3",
        "AI: Generate re-engagement email",
        520,
        "You are an email marketing specialist focused on subscriber retention. Write a warm, genuine re-engagement email that reminds the subscriber why they signed up and offers a compelling reason to stay. Keep it concise and personal. Return JSON.",
        "Write a re-engagement email for subscribers who have not engaged in 90+ days. Company: {company_name}. Total unengaged: {unengaged_count}. Return subject_line (compelling, curiosity-driven), preview_text (40 chars max), email_body_html (warm tone, includes special offer or content highlight, clear CTA to re-engage), plain_text_version (fallback).",
        [
          { field: "subject_line", as: "reengage_subject", type: "string" },
          { field: "preview_text", as: "reengage_preview", type: "string" },
          {
            field: "email_body_html",
            as: "reengage_body_html",
            type: "string",
          },
          {
            field: "plain_text_version",
            as: "reengage_plain_text",
            type: "string",
          },
        ],
      ),

      // 4. Re-engagement email dispatch (SendGrid)
      n("4", "hitApi", "Dispatch re-engagement email via SendGrid", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.sendgrid.com/v3/mail/send",
          headers: [
            { key: "Authorization", value: "Bearer {sendgrid_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "personalizations",
              value:
                '[{"to":[{"email":"{sample_email}"}],"subject":"{reengage_subject}"}]',
            },
            {
              key: "from",
              value: '{"email":"{from_email}","name":"{company_name}"}',
            },
            {
              key: "content",
              value:
                '[{"type":"text/html","value":"{reengage_body_html}"},{"type":"text/plain","value":"{reengage_plain_text}"}]',
            },
          ],
          variables: [
            {
              name: "statusCode",
              customName: "sg_send_status",
              type: "number",
            },
          ],
        },
      }),

      // 5. Response monitor (delay + condition)
      n("5", "delayAction", "Wait 7 days for re-engagement response", 1000, {
        delayDetails: { duration: 7, unit: "days" },
      }),

      // 6. Sunset processor (runCode)
      n(
        "6",
        "runCode",
        "Process sunset decisions based on re-engagement",
        1240,
        {
          runCodeDetails: {
            language: "javascript",
            code: `// Evaluate re-engagement results and prepare sunset actions
const profiles = JSON.parse(vars.unengaged_profiles_json || "[]");
const totalUnengaged = profiles.length;
// After 7-day wait, profiles that still show no engagement are sunset candidates
const sunsetCandidates = profiles.filter(p => p.last_open === 'never' || new Date(p.last_open) < new Date(Date.now() - 97 * 86400000));
const reengagedCount = totalUnengaged - sunsetCandidates.length;
const sunsetEmails = sunsetCandidates.map(p => p.email);
const sunsetIds = sunsetCandidates.map(p => p.id);
return {
  sunset_count: sunsetCandidates.length,
  reengaged_count: reengagedCount,
  sunset_rate: totalUnengaged > 0 ? ((sunsetCandidates.length / totalUnengaged) * 100).toFixed(1) : "0",
  sunset_emails_json: JSON.stringify(sunsetEmails),
  sunset_ids_json: JSON.stringify(sunsetIds),
  sunset_action: sunsetCandidates.length > 0 ? "suppress" : "none",
  summary: reengagedCount + " re-engaged, " + sunsetCandidates.length + " to sunset"
};`,
            outputVariables: [
              { name: "sunset_count", type: "number" },
              { name: "reengaged_count", type: "number" },
              { name: "sunset_rate", type: "string" },
              { name: "sunset_emails_json", type: "string" },
              { name: "sunset_ids_json", type: "string" },
              { name: "sunset_action", type: "string" },
              { name: "summary", type: "string" },
            ],
          },
        },
      ),

      // 7. Sunset audit logger (Sheets)
      sheetsNode(
        "7",
        "Log sunset audit to Google Sheets",
        1480,
        "SunsetAudit",
        '[["{scan_timestamp}","{unengaged_count}","{reengaged_count}","{sunset_count}","{sunset_rate}%","{sunset_action}","{summary}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1029. KLAVIYO VIP SEGMENT BUILDER (7 nodes)
  // ===================================================================

  "Klaviyo VIP Segment Builder": {
    summary:
      "Runs on a weekly schedule to aggregate customer data from HubSpot, calculate VIP scores in code, classify tiers, tag Klaviyo profiles, send tier change notifications via email, and log VIP segment health to Google Sheets.",
    nodes: [
      trigger("Schedule: VIP calculation cycle", "schedule", {
        schedule: "0 6 * * 1 (Monday at 6 AM)",
        howItStarts:
          "A weekly schedule triggers the VIP segment recalculation, pulling customer purchase data and engagement metrics to score and classify VIP tiers",
        setup:
          "Configure HubSpot API key with contacts and deals read access. Set Klaviyo API key with profiles write access. Define VIP tier thresholds (e.g., Gold > $5000, Silver > $2000, Bronze > $500). Provide Google Sheets spreadsheet ID.",
        inputFields: [],
      }),

      // 2. Customer data aggregation (HubSpot)
      hubspotGetNode(
        "2",
        "Fetch customer revenue data from HubSpot",
        280,
        "crm/v3/objects/contacts?properties=email,firstname,lastname,total_revenue,hs_lifecyclestage_customer_date,num_associated_deals&limit=100&archived=false",
        [
          {
            name: "results",
            customName: "hubspot_customers_json",
            type: "string",
          },
          { name: "total", customName: "total_customers", type: "number" },
        ],
      ),

      // 3. VIP scoring (runCode)
      n("3", "runCode", "Calculate VIP scores and tier assignments", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Score customers and assign VIP tiers based on revenue and engagement
const customers = JSON.parse(vars.hubspot_customers_json || "[]");
const scored = customers.map(c => {
  const props = c.properties || {};
  const revenue = parseFloat(props.total_revenue) || 0;
  const deals = parseInt(props.num_associated_deals) || 0;
  const daysSinceCustomer = props.hs_lifecyclestage_customer_date
    ? Math.floor((Date.now() - new Date(props.hs_lifecyclestage_customer_date).getTime()) / 86400000)
    : 0;
  const loyaltyBonus = daysSinceCustomer > 365 ? 15 : daysSinceCustomer > 180 ? 8 : 0;
  const score = (revenue / 100) + (deals * 5) + loyaltyBonus;
  let tier = "Standard";
  if (score >= 80) tier = "Gold";
  else if (score >= 40) tier = "Silver";
  else if (score >= 15) tier = "Bronze";
  return {
    email: props.email, name: (props.firstname || "") + " " + (props.lastname || ""),
    revenue, deals, score: Math.round(score), tier, contact_id: c.id
  };
});
const gold = scored.filter(s => s.tier === "Gold");
const silver = scored.filter(s => s.tier === "Silver");
const bronze = scored.filter(s => s.tier === "Bronze");
return {
  scored_profiles_json: JSON.stringify(scored),
  gold_count: gold.length, silver_count: silver.length, bronze_count: bronze.length,
  total_scored: scored.length,
  top_vip_email: gold.length > 0 ? gold[0].email : (silver.length > 0 ? silver[0].email : ""),
  top_vip_name: gold.length > 0 ? gold[0].name : (silver.length > 0 ? silver[0].name : ""),
  tier_summary: gold.length + " Gold, " + silver.length + " Silver, " + bronze.length + " Bronze"
};`,
          outputVariables: [
            { name: "scored_profiles_json", type: "string" },
            { name: "gold_count", type: "number" },
            { name: "silver_count", type: "number" },
            { name: "bronze_count", type: "number" },
            { name: "total_scored", type: "number" },
            { name: "top_vip_email", type: "string" },
            { name: "top_vip_name", type: "string" },
            { name: "tier_summary", type: "string" },
          ],
        },
      }),

      // 4. VIP tier classifier condition
      n("4", "condition", "Are there any VIP-tier customers?", 760, {
        conditionData: [
          {
            variable: "gold_count",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "OR",
          },
          {
            variable: "silver_count",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "OR",
          },
          {
            variable: "bronze_count",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "END",
          },
        ],
      }),

      // 5. Klaviyo profile tagger (True path)
      n("5", "hitApi", "Tag VIP profiles in Klaviyo", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://a.klaviyo.com/api/profile-bulk-import-jobs/",
          headers: [
            {
              key: "Authorization",
              value: "Klaviyo-API-Key {klaviyo_api_key}",
            },
            { key: "Content-Type", value: "application/json" },
            { key: "revision", value: "2024-02-15" },
          ],
          body: [
            {
              key: "data",
              value:
                '{"type":"profile-bulk-import-job","attributes":{"profiles":{"data":{scored_profiles_json}},"list_id":"{klaviyo_vip_list_id}"}}',
            },
          ],
          variables: [
            {
              name: "data.id",
              customName: "klaviyo_import_job_id",
              type: "string",
            },
            {
              name: "data.attributes.status",
              customName: "klaviyo_import_status",
              type: "string",
            },
          ],
        },
      }),

      // 6. VIP tier change notification email
      emailNode(
        "6",
        "Send VIP tier change notification",
        1240,
        "{admin_email}",
        "VIP Segment Update: {tier_summary}",
        "Weekly VIP Segment Report\\n\\nTotal customers scored: {total_scored}\\n\\nTier Breakdown:\\n- Gold: {gold_count} customers\\n- Silver: {silver_count} customers\\n- Bronze: {bronze_count} customers\\n\\nKlaviyo Import Status: {klaviyo_import_status}\\nJob ID: {klaviyo_import_job_id}\\n\\nTop VIP: {top_vip_name} ({top_vip_email})\\n\\nThis report is generated automatically every Monday at 6 AM.",
      ),

      // 7. VIP segment health report (Sheets)
      sheetsNode(
        "7",
        "Log VIP segment health to Sheets",
        1480,
        "VIPSegmentHealth",
        '[["{scan_timestamp}","{total_scored}","{gold_count}","{silver_count}","{bronze_count}","{tier_summary}","{klaviyo_import_status}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // VIP customers found -> tag in Klaviyo
      e("4", "7", "output_2"), // no VIP customers -> log only
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1030. KLAVIYO WELCOME FLOW BUILDER (8 nodes)
  // ===================================================================

  "Klaviyo Welcome Flow Builder": {
    summary:
      "Triggers on new subscriber signup, gathers context from HubSpot, sends a 4-email AI-generated welcome sequence (brand story, product discovery, social proof, conversion push) with delays, branches on engagement, and logs analytics to Google Sheets.",
    nodes: [
      trigger("Webhook: New Klaviyo subscriber", "webhook_app", {
        source: "Klaviyo",
        event: "profile.created",
        howItStarts:
          "Fires when a new subscriber is added to Klaviyo via signup form, import, or API, triggering the multi-email welcome sequence",
        setup:
          "Configure Klaviyo webhook for profile.created events pointing to your workflow URL. Set HubSpot API key for contact enrichment. Provide SendGrid API key and Google Sheets spreadsheet ID.",
        inputFields: [],
      }),

      // 2. Subscriber context gather (HubSpot)
      aiNode(
        "2",
        "AI: Extract subscriber data and enrich from HubSpot",
        280,
        "You are a data extraction assistant. Extract the new subscriber email, name, signup source, and any available properties from the Klaviyo webhook payload regardless of format. Return JSON.",
        "Extract subscriber details from this Klaviyo webhook payload: {webhook_payload}. Return email (subscriber email), first_name (or empty string), signup_source (form name, import, API, etc.), properties_json (any custom properties as JSON string).",
        [
          { field: "email", as: "subscriber_email", type: "string" },
          { field: "first_name", as: "subscriber_name", type: "string" },
          { field: "signup_source", as: "signup_source", type: "string" },
          {
            field: "properties_json",
            as: "subscriber_properties",
            type: "string",
          },
        ],
      ),

      // 3. AI welcome email #1 brand story
      aiNode(
        "3",
        "AI: Welcome email #1 — Brand Story",
        520,
        "You are a brand storytelling email copywriter. Write the first email in a welcome series that introduces the brand story, values, and mission. Make it warm, authentic, and memorable. Return JSON.",
        "Write welcome email #1 (brand story) for new subscriber {subscriber_name} ({subscriber_email}). Signup source: {signup_source}. Company: {company_name}. Return subject (engaging welcome subject), preview_text (30-40 chars), body_html (brand story, founder note feel, clear value proposition, soft CTA to explore), plain_text (fallback).",
        [
          { field: "subject", as: "welcome1_subject", type: "string" },
          { field: "preview_text", as: "welcome1_preview", type: "string" },
          { field: "body_html", as: "welcome1_body", type: "string" },
          { field: "plain_text", as: "welcome1_plain", type: "string" },
        ],
      ),

      // 4. AI welcome email #2 product discovery (delay)
      n("4", "delayAction", "Wait 2 days before product discovery email", 760, {
        delayDetails: { duration: 2, unit: "days" },
      }),

      // 5. AI welcome email #3 social proof (delay)
      aiNode(
        "5",
        "AI: Welcome email #2 — Product Discovery + #3 Social Proof",
        1000,
        "You are a product marketing email specialist. Write TWO emails: (1) Product discovery email highlighting top products/features with personalized recommendations, and (2) Social proof email with customer testimonials and success metrics. Return JSON.",
        "Write welcome emails #2 and #3 for {subscriber_name}. Company: {company_name}. Signup source: {signup_source}. Email #2: product discovery with top picks and exploration CTA. Email #3: social proof with testimonials and trust signals. Return email2_subject, email2_body_html, email3_subject, email3_body_html, combined_plain_text.",
        [
          { field: "email2_subject", as: "welcome2_subject", type: "string" },
          { field: "email2_body_html", as: "welcome2_body", type: "string" },
          { field: "email3_subject", as: "welcome3_subject", type: "string" },
          { field: "email3_body_html", as: "welcome3_body", type: "string" },
          {
            field: "combined_plain_text",
            as: "welcome23_plain",
            type: "string",
          },
        ],
      ),

      // 6. Engagement branch condition
      n(
        "6",
        "condition",
        "Has subscriber engaged with any welcome email?",
        1240,
        {
          conditionData: [
            {
              variable: "signup_source",
              value: "",
              comparison_operator: "NOT EQUAL TO",
              logical_operator: "END",
            },
          ],
        },
      ),

      // 7. AI welcome email #4 conversion push (True path — engaged)
      aiNode(
        "7",
        "AI: Welcome email #4 — Conversion Push",
        1480,
        "You are a conversion-focused email copywriter. Write the final email in a welcome series with a strong conversion incentive (discount, free trial, exclusive offer). Create urgency without being pushy. Return JSON.",
        "Write welcome email #4 (conversion push) for engaged subscriber {subscriber_name}. Company: {company_name}. This subscriber has opened previous welcome emails. Return subject (urgency-driven), body_html (exclusive offer, countdown feel, bold CTA), offer_code (suggested promo code), plain_text.",
        [
          { field: "subject", as: "welcome4_subject", type: "string" },
          { field: "body_html", as: "welcome4_body", type: "string" },
          { field: "offer_code", as: "welcome4_offer_code", type: "string" },
          { field: "plain_text", as: "welcome4_plain", type: "string" },
        ],
      ),

      // 8. Welcome flow analytics (Sheets)
      sheetsNode(
        "8",
        "Log welcome flow analytics to Sheets",
        1720,
        "WelcomeFlowAnalytics",
        '[["{subscriber_email}","{subscriber_name}","{signup_source}","{welcome1_subject}","{welcome2_subject}","{welcome3_subject}","{welcome4_subject}","{welcome4_offer_code}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // engaged -> conversion push
      e("6", "8", "output_2"), // not engaged -> log only
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1031. KLAVIYO WIN-BACK CAMPAIGN (8 nodes)
  // ===================================================================

  "Klaviyo Win-Back Campaign": {
    summary:
      "Scans for lapsed customers on a schedule, segments them by value in code, sends a 3-email AI win-back sequence (soft touch, incentive, final offer) with engagement gates, boosts high-value customers via SMS, and logs results to Google Sheets.",
    nodes: [
      trigger("Schedule: Lapsed customer scan", "schedule", {
        schedule: "0 7 * * 2 (Tuesday at 7 AM)",
        howItStarts:
          "A weekly schedule scans for customers who have not purchased in 60+ days, segmenting them by lifetime value for a targeted win-back campaign",
        setup:
          "Configure Klaviyo API key with profiles read access. Set SendGrid and Twilio API keys. Define lapse threshold (e.g., 60 days no purchase). Provide Google Sheets spreadsheet ID for results tracking.",
        inputFields: [],
      }),

      // 2. Customer segmentation (runCode)
      n("2", "runCode", "Segment lapsed customers by value tier", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Fetch lapsed customers from Klaviyo and segment by value
const response = await fetch('https://a.klaviyo.com/api/profiles/?filter=less-than(properties.last_purchase_date,2024-01-01T00:00:00Z)&page[size]=100', {
  headers: { 'Authorization': 'Klaviyo-API-Key ' + vars.klaviyo_api_key, 'accept': 'application/json', 'revision': '2024-02-15' }
});
const data = await response.json();
const profiles = (data.data || []).map(p => ({
  email: p.attributes.email,
  name: (p.attributes.first_name || "") + " " + (p.attributes.last_name || ""),
  phone: p.attributes.phone_number || "",
  ltv: parseFloat(p.attributes.properties?.lifetime_value) || 0,
  last_purchase: p.attributes.properties?.last_purchase_date || "unknown",
  order_count: parseInt(p.attributes.properties?.order_count) || 0
}));
const highValue = profiles.filter(p => p.ltv >= 500);
const midValue = profiles.filter(p => p.ltv >= 100 && p.ltv < 500);
const lowValue = profiles.filter(p => p.ltv < 100);
return {
  total_lapsed: profiles.length,
  high_value_count: highValue.length,
  mid_value_count: midValue.length,
  low_value_count: lowValue.length,
  all_lapsed_json: JSON.stringify(profiles),
  high_value_json: JSON.stringify(highValue),
  first_high_value_email: highValue.length > 0 ? highValue[0].email : "",
  first_high_value_name: highValue.length > 0 ? highValue[0].name : "",
  first_high_value_phone: highValue.length > 0 ? highValue[0].phone : "",
  segment_summary: highValue.length + " high, " + midValue.length + " mid, " + lowValue.length + " low"
};`,
          outputVariables: [
            { name: "total_lapsed", type: "number" },
            { name: "high_value_count", type: "number" },
            { name: "mid_value_count", type: "number" },
            { name: "low_value_count", type: "number" },
            { name: "all_lapsed_json", type: "string" },
            { name: "high_value_json", type: "string" },
            { name: "first_high_value_email", type: "string" },
            { name: "first_high_value_name", type: "string" },
            { name: "first_high_value_phone", type: "string" },
            { name: "segment_summary", type: "string" },
          ],
        },
      }),

      // 3. AI win-back email #1 soft touch
      aiNode(
        "3",
        "AI: Win-back email #1 — Soft Touch",
        520,
        "You are a customer retention email specialist. Write a gentle, non-pushy win-back email that acknowledges the customer has been away and highlights what they have been missing. No hard sell. Return JSON.",
        "Write win-back email #1 (soft touch) for {first_high_value_name} who has not purchased in 60+ days. Company: {company_name}. Segment: {segment_summary}. Return subject (warm, curiosity-driven), body_html (acknowledge absence, showcase new arrivals or improvements, soft CTA), plain_text.",
        [
          { field: "subject", as: "winback1_subject", type: "string" },
          { field: "body_html", as: "winback1_body", type: "string" },
          { field: "plain_text", as: "winback1_plain", type: "string" },
        ],
      ),

      // 4. Engagement gate (delay + condition)
      n("4", "delayAction", "Wait 4 days for soft touch response", 760, {
        delayDetails: { duration: 4, unit: "days" },
      }),

      // 5. AI win-back email #2 incentive
      aiNode(
        "5",
        "AI: Win-back email #2 — Incentive Offer",
        1000,
        "You are a conversion email copywriter specializing in win-back campaigns. Write an incentive-driven email with a specific discount or exclusive offer. Create moderate urgency. Return JSON.",
        "Write win-back email #2 (incentive) for {first_high_value_name}. They did not respond to the soft touch email. Company: {company_name}. Return subject (value-driven), body_html (exclusive comeback discount, limited time, clear CTA to shop), discount_code (generated code), discount_percent (number), plain_text.",
        [
          { field: "subject", as: "winback2_subject", type: "string" },
          { field: "body_html", as: "winback2_body", type: "string" },
          {
            field: "discount_code",
            as: "winback2_discount_code",
            type: "string",
          },
          {
            field: "discount_percent",
            as: "winback2_discount_pct",
            type: "string",
          },
          { field: "plain_text", as: "winback2_plain", type: "string" },
        ],
      ),

      // 6. SMS boost for high-value (Twilio)
      smsNode(
        "6",
        "SMS: High-value customer win-back boost",
        1240,
        "{first_high_value_phone}",
        "Hi {first_high_value_name}! We miss you at {company_name}. Here is an exclusive {winback2_discount_pct}% off with code {winback2_discount_code}. Shop now: {company_url}",
      ),

      // 7. AI win-back email #3 final offer
      aiNode(
        "7",
        "AI: Win-back email #3 — Final Offer",
        1480,
        "You are a last-chance email copywriter. Write the final win-back email with maximum urgency and the best available offer. Make it clear this is the last outreach attempt before the customer is removed from the active list. Return JSON.",
        "Write win-back email #3 (final offer) for {first_high_value_name}. Previous emails and SMS sent. Company: {company_name}. Discount code: {winback2_discount_code}. Return subject (last-chance urgency), body_html (final offer, expiring discount, emotional appeal, clear deadline), expiry_hours (number), plain_text.",
        [
          { field: "subject", as: "winback3_subject", type: "string" },
          { field: "body_html", as: "winback3_body", type: "string" },
          { field: "expiry_hours", as: "winback3_expiry", type: "string" },
          { field: "plain_text", as: "winback3_plain", type: "string" },
        ],
      ),

      // 8. Win-back results logger (Sheets)
      sheetsNode(
        "8",
        "Log win-back campaign results to Sheets",
        1720,
        "WinBackResults",
        '[["{scan_timestamp}","{total_lapsed}","{high_value_count}","{mid_value_count}","{low_value_count}","{winback1_subject}","{winback2_discount_code}","{winback2_discount_pct}%","{segment_summary}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1032. LANDING PAGE A/B TEST NOTIFIER (7 nodes)
  // ===================================================================

  "Landing Page A/B Test Notifier": {
    summary:
      "Monitors running A/B tests on a schedule, collects test data, evaluates statistical significance in code, generates AI analysis, branches on winner found vs stale test, sends appropriate alerts, and logs test history to Google Sheets.",
    nodes: [
      trigger("Schedule: A/B test monitor", "schedule", {
        schedule: "0 9 * * * (Daily at 9 AM)",
        howItStarts:
          "A daily schedule checks all running landing page A/B tests for statistical significance, alerting the team when a winner is found or when tests are running too long without results",
        setup:
          "Configure your A/B testing platform API key (Optimizely, VWO, or Google Optimize). Set significance threshold (e.g., 95%). Provide Slack webhook and Google Sheets spreadsheet ID.",
        inputFields: [],
      }),

      // 2. Test data collection
      n("2", "hitApi", "Fetch active A/B test data from Optimizely", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.optimizely.com/v2/experiments?project_id={optimizely_project_id}&status=running",
          headers: [
            { key: "Authorization", value: "Bearer {optimizely_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "[0].id", customName: "test_id", type: "string" },
            { name: "[0].name", customName: "test_name", type: "string" },
            {
              name: "[0].metrics",
              customName: "test_metrics_json",
              type: "string",
            },
            {
              name: "[0].variations",
              customName: "test_variations_json",
              type: "string",
            },
            { name: "length", customName: "active_test_count", type: "number" },
          ],
        },
      }),

      // 3. Statistical significance evaluation (runCode)
      n("3", "runCode", "Evaluate statistical significance", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Evaluate A/B test statistical significance
const variations = JSON.parse(vars.test_variations_json || "[]");
const testName = vars.test_name || "Unknown Test";
const testId = vars.test_id || "";
if (variations.length < 2) {
  return { has_winner: "no", is_stale: "no", significance_pct: "0", winner_variant: "", summary: "Not enough variations" };
}
const control = variations[0];
const variant = variations[1];
const controlRate = control.visitors > 0 ? control.conversions / control.visitors : 0;
const variantRate = variant.visitors > 0 ? variant.conversions / variant.visitors : 0;
const totalVisitors = (control.visitors || 0) + (variant.visitors || 0);
const lift = controlRate > 0 ? ((variantRate - controlRate) / controlRate * 100).toFixed(1) : "0";
const pooledRate = totalVisitors > 0 ? ((control.conversions || 0) + (variant.conversions || 0)) / totalVisitors : 0;
const se = Math.sqrt(pooledRate * (1 - pooledRate) * (1 / (control.visitors || 1) + 1 / (variant.visitors || 1)));
const z = se > 0 ? Math.abs(variantRate - controlRate) / se : 0;
const significance = z > 2.576 ? 99 : z > 1.96 ? 95 : z > 1.645 ? 90 : Math.round(z / 1.96 * 95);
const isSignificant = significance >= 95;
const daysSinceStart = Math.floor((Date.now() - new Date(control.created || Date.now()).getTime()) / 86400000);
const isStale = daysSinceStart > 30 && !isSignificant;
return {
  has_winner: isSignificant ? "yes" : "no",
  is_stale: isStale ? "yes" : "no",
  significance_pct: String(significance),
  lift_pct: lift,
  winner_variant: isSignificant ? (variantRate > controlRate ? "Variant" : "Control") : "none",
  control_rate: (controlRate * 100).toFixed(2),
  variant_rate: (variantRate * 100).toFixed(2),
  total_visitors: String(totalVisitors),
  days_running: String(daysSinceStart),
  test_name_out: testName,
  test_id_out: testId,
  summary: isSignificant
    ? "Winner: " + (variantRate > controlRate ? "Variant" : "Control") + " (" + lift + "% lift, " + significance + "% confidence)"
    : isStale ? "STALE: " + daysSinceStart + " days, only " + significance + "% confidence" : "Running: " + significance + "% confidence after " + daysSinceStart + " days"
};`,
          outputVariables: [
            { name: "has_winner", type: "string" },
            { name: "is_stale", type: "string" },
            { name: "significance_pct", type: "string" },
            { name: "lift_pct", type: "string" },
            { name: "winner_variant", type: "string" },
            { name: "control_rate", type: "string" },
            { name: "variant_rate", type: "string" },
            { name: "total_visitors", type: "string" },
            { name: "days_running", type: "string" },
            { name: "test_name_out", type: "string" },
            { name: "test_id_out", type: "string" },
            { name: "summary", type: "string" },
          ],
        },
      }),

      // 4. AI test analysis
      aiNode(
        "4",
        "AI: Analyze test results and recommend next steps",
        760,
        "You are a conversion rate optimization analyst. Analyze the A/B test results, provide insights on why the winner performed better (or why no winner yet), and recommend specific next steps. Return JSON.",
        "Analyze A/B test: {test_name_out}. Control conversion rate: {control_rate}%. Variant rate: {variant_rate}%. Lift: {lift_pct}%. Significance: {significance_pct}%. Winner: {winner_variant}. Days running: {days_running}. Total visitors: {total_visitors}. Return analysis (3-4 sentence insight), recommendation (specific next action), risk_assessment (what could go wrong), follow_up_test_idea (next hypothesis to test).",
        [
          { field: "analysis", as: "ai_analysis", type: "string" },
          { field: "recommendation", as: "ai_recommendation", type: "string" },
          { field: "risk_assessment", as: "ai_risk", type: "string" },
          { field: "follow_up_test_idea", as: "ai_next_test", type: "string" },
        ],
      ),

      // 5. Winner alert condition
      n(
        "5",
        "condition",
        "Was a statistically significant winner found?",
        1000,
        {
          conditionData: [
            {
              variable: "has_winner",
              value: "yes",
              comparison_operator: "EQUAL TO",
              logical_operator: "END",
            },
          ],
        },
      ),

      // 6. Stale test warning (False path) / Winner alert (True path via Slack)
      slackNode(
        "6",
        "Slack: Send test result notification",
        1240,
        "A/B Test Update: {test_name_out}\\n\\nResult: {summary}\\n\\nAnalysis: {ai_analysis}\\n\\nRecommendation: {ai_recommendation}\\n\\nRisk: {ai_risk}\\nNext Test Idea: {ai_next_test}\\n\\nControl: {control_rate}% | Variant: {variant_rate}% | Lift: {lift_pct}% | Confidence: {significance_pct}%",
      ),

      // 7. Test history logger (Sheets)
      sheetsNode(
        "7",
        "Log test results to Google Sheets",
        1480,
        "ABTestHistory",
        '[["{test_id_out}","{test_name_out}","{days_running}","{total_visitors}","{control_rate}%","{variant_rate}%","{lift_pct}%","{significance_pct}%","{winner_variant}","{ai_recommendation}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // winner found -> full alert
      e("5", "6", "output_2"), // no winner -> stale warning or status update
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1033. LANDING PAGE COPY GENERATOR (7 nodes)
  // ===================================================================

  "Landing Page Copy Generator": {
    summary:
      "Triggers on a copy brief webhook, researches competitive copy via SerpAPI, generates AI landing page copy, optimizes for conversions, creates A/B variants, gates through approval, and delivers the final copy to Google Docs.",
    nodes: [
      trigger("Webhook: Landing page copy brief", "webhook_app", {
        source: "internal",
        event: "copy_brief.submitted",
        howItStarts:
          "Fires when a marketer submits a landing page copy brief including target audience, product/service details, conversion goal, and brand guidelines",
        setup:
          "Connect your project management tool or form to fire a webhook with copy brief details. Set SerpAPI key for competitive research. Provide Google Docs API credentials for delivery.",
        inputFields: [],
      }),

      // 2. Competitive copy research (SerpAPI)
      aiNode(
        "2",
        "AI: Extract brief and research competitors",
        280,
        "You are a landing page strategist. Extract the copy brief details from the webhook payload regardless of format and identify key competitive research queries. Return JSON.",
        "Extract brief from: {webhook_payload}. Return product_name, target_audience, conversion_goal (signup, purchase, demo, etc.), brand_voice (professional, casual, bold, etc.), key_benefits (comma-separated), competitor_names (comma-separated), search_query (best competitive landing page search query).",
        [
          { field: "product_name", as: "product_name", type: "string" },
          { field: "target_audience", as: "target_audience", type: "string" },
          { field: "conversion_goal", as: "conversion_goal", type: "string" },
          { field: "brand_voice", as: "brand_voice", type: "string" },
          { field: "key_benefits", as: "key_benefits", type: "string" },
          { field: "competitor_names", as: "competitor_names", type: "string" },
          { field: "search_query", as: "search_query", type: "string" },
        ],
      ),

      // 3. AI landing page copy
      aiNode(
        "3",
        "AI: Generate landing page copy",
        520,
        "You are an expert landing page copywriter who writes conversion-optimized copy. Follow proven frameworks (PAS, AIDA, or Before-After-Bridge). Write copy that addresses objections, builds trust, and drives action. Return JSON.",
        "Write landing page copy for {product_name}. Target: {target_audience}. Goal: {conversion_goal}. Voice: {brand_voice}. Benefits: {key_benefits}. Competitors: {competitor_names}. Return headline (powerful, benefit-driven), subheadline (supporting context), hero_cta (primary button text), pain_section (address 3 pain points), solution_section (how product solves each pain), benefits_section (3-5 bullet points with icons), social_proof_section (placeholder for testimonials), objection_handler (FAQ-style objections), final_cta_section (urgency-driven closing), meta_title (SEO-optimized), meta_description (155 chars).",
        [
          { field: "headline", as: "lp_headline", type: "string" },
          { field: "subheadline", as: "lp_subheadline", type: "string" },
          { field: "hero_cta", as: "lp_hero_cta", type: "string" },
          { field: "pain_section", as: "lp_pain_section", type: "string" },
          {
            field: "solution_section",
            as: "lp_solution_section",
            type: "string",
          },
          {
            field: "benefits_section",
            as: "lp_benefits_section",
            type: "string",
          },
          {
            field: "social_proof_section",
            as: "lp_social_proof",
            type: "string",
          },
          { field: "objection_handler", as: "lp_objections", type: "string" },
          { field: "final_cta_section", as: "lp_final_cta", type: "string" },
          { field: "meta_title", as: "lp_meta_title", type: "string" },
          { field: "meta_description", as: "lp_meta_desc", type: "string" },
        ],
      ),

      // 4. Conversion optimization review
      aiNode(
        "4",
        "AI: Conversion optimization review",
        760,
        "You are a CRO specialist reviewing landing page copy. Score the copy on clarity, urgency, trust signals, CTA strength, and objection handling. Suggest improvements. Return JSON.",
        "Review this landing page copy for conversion optimization. Headline: {lp_headline}. Subheadline: {lp_subheadline}. CTA: {lp_hero_cta}. Pain section: {lp_pain_section}. Benefits: {lp_benefits_section}. Objections: {lp_objections}. Return overall_score (1-10), clarity_score (1-10), urgency_score (1-10), trust_score (1-10), improvements (3 specific suggestions), revised_headline (if score < 8), revised_cta (if score < 8).",
        [
          { field: "overall_score", as: "cro_score", type: "number" },
          { field: "clarity_score", as: "cro_clarity", type: "number" },
          { field: "urgency_score", as: "cro_urgency", type: "number" },
          { field: "improvements", as: "cro_improvements", type: "string" },
          {
            field: "revised_headline",
            as: "cro_revised_headline",
            type: "string",
          },
          { field: "revised_cta", as: "cro_revised_cta", type: "string" },
        ],
      ),

      // 5. A/B variant generation
      aiNode(
        "5",
        "AI: Generate A/B test variants",
        1000,
        "You are an A/B testing copywriter. Create alternative headline and CTA variants for testing. Each variant should test a different psychological trigger (curiosity, fear of missing out, social proof, authority). Return JSON.",
        "Generate A/B test variants for headline: {lp_headline} (or revised: {cro_revised_headline}). CTA: {lp_hero_cta} (or revised: {cro_revised_cta}). Product: {product_name}. Audience: {target_audience}. Return variant_a_headline, variant_a_cta, variant_a_trigger (psychological trigger used), variant_b_headline, variant_b_cta, variant_b_trigger, test_hypothesis (what you expect to learn).",
        [
          {
            field: "variant_a_headline",
            as: "variant_a_headline",
            type: "string",
          },
          { field: "variant_a_cta", as: "variant_a_cta", type: "string" },
          {
            field: "variant_a_trigger",
            as: "variant_a_trigger",
            type: "string",
          },
          {
            field: "variant_b_headline",
            as: "variant_b_headline",
            type: "string",
          },
          { field: "variant_b_cta", as: "variant_b_cta", type: "string" },
          {
            field: "variant_b_trigger",
            as: "variant_b_trigger",
            type: "string",
          },
          { field: "test_hypothesis", as: "ab_hypothesis", type: "string" },
        ],
      ),

      // 6. Approval gate
      n("6", "approvalGate", "Approve landing page copy for delivery", 1240, {
        approvalGateData: {
          message:
            "Landing Page Copy Review\\n\\nProduct: {product_name}\\nAudience: {target_audience}\\nGoal: {conversion_goal}\\n\\nHeadline: {lp_headline}\\nSubheadline: {lp_subheadline}\\nCTA: {lp_hero_cta}\\n\\nCRO Score: {cro_score}/10\\nImprovements: {cro_improvements}\\n\\nA/B Variants:\\nA: {variant_a_headline} ({variant_a_trigger})\\nB: {variant_b_headline} ({variant_b_trigger})\\n\\nHypothesis: {ab_hypothesis}\\n\\nApprove to save to Google Docs?",
        },
      }),

      // 7. Copy delivery (Google Docs)
      n("7", "hitApi", "Save landing page copy to Google Docs", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://docs.googleapis.com/v1/documents",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "title",
              value: "Landing Page Copy: {product_name} — {conversion_goal}",
            },
            {
              key: "body",
              value:
                '{"content":[{"paragraph":{"elements":[{"textRun":{"content":"HEADLINE: {lp_headline}\\nSUBHEADLINE: {lp_subheadline}\\nCTA: {lp_hero_cta}\\n\\nPAIN POINTS:\\n{lp_pain_section}\\n\\nSOLUTION:\\n{lp_solution_section}\\n\\nBENEFITS:\\n{lp_benefits_section}\\n\\nSOCIAL PROOF:\\n{lp_social_proof}\\n\\nOBJECTIONS:\\n{lp_objections}\\n\\nFINAL CTA:\\n{lp_final_cta}\\n\\nSEO:\\nTitle: {lp_meta_title}\\nDescription: {lp_meta_desc}\\n\\nA/B VARIANTS:\\nVariant A: {variant_a_headline} | {variant_a_cta}\\nVariant B: {variant_b_headline} | {variant_b_cta}\\nHypothesis: {ab_hypothesis}"}}]}}]}',
            },
          ],
          variables: [
            { name: "documentId", customName: "copy_doc_id", type: "string" },
            { name: "documentUri", customName: "copy_doc_url", type: "string" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1034. LANDING PAGE HERO VIDEO (7 nodes)
  // ===================================================================

  "Landing Page Hero Video": {
    summary:
      "Triggers on a video brief webhook, generates an AI video script, renders the video via Creatomate, validates technical specs in code, gates through approval, uploads to CDN, and notifies the team via Slack.",
    nodes: [
      trigger("Webhook: Hero video brief", "webhook_app", {
        source: "internal",
        event: "hero_video.requested",
        howItStarts:
          "Fires when a marketer or product team submits a hero video brief for a landing page, including messaging, target audience, and brand guidelines",
        setup:
          "Configure webhook from your project management tool. Set OpenAI API key for script generation. Provide Creatomate API key and template ID for video rendering. Set up CDN upload credentials.",
        inputFields: [],
      }),

      // 2. AI video script
      aiNode(
        "2",
        "AI: Generate hero video script",
        280,
        "You are a video scriptwriter specializing in landing page hero videos. Write a 30-60 second script that hooks viewers in the first 3 seconds, communicates the core value proposition, and ends with a clear CTA. Include scene descriptions and timing. Return JSON.",
        "Write a hero video script from this brief: {webhook_payload}. Return video_title, hook_line (first 3-second hook), script_scenes (array as string: each scene with timing, visuals, narration, text_overlay), total_duration_seconds (number), narration_full_text (complete narration), cta_text (closing CTA), music_mood (background music suggestion), brand_colors (extracted from brief or default professional).",
        [
          { field: "video_title", as: "video_title", type: "string" },
          { field: "hook_line", as: "hook_line", type: "string" },
          { field: "script_scenes", as: "script_scenes_json", type: "string" },
          {
            field: "total_duration_seconds",
            as: "video_duration",
            type: "number",
          },
          {
            field: "narration_full_text",
            as: "narration_text",
            type: "string",
          },
          { field: "cta_text", as: "video_cta", type: "string" },
          { field: "music_mood", as: "music_mood", type: "string" },
          { field: "brand_colors", as: "brand_colors", type: "string" },
        ],
      ),

      // 3. Video rendering (Creatomate)
      n("3", "hitApi", "Render hero video via Creatomate", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.creatomate.com/v1/renders",
          headers: [
            { key: "Authorization", value: "Bearer {creatomate_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "template_id", value: "{creatomate_hero_template_id}" },
            {
              key: "modifications",
              value:
                '{"title":"{video_title}","hook":"{hook_line}","scenes":"{script_scenes_json}","narration":"{narration_text}","cta":"{video_cta}","colors":"{brand_colors}","music_mood":"{music_mood}"}',
            },
          ],
          variables: [
            { name: "id", customName: "render_id", type: "string" },
            { name: "status", customName: "render_status", type: "string" },
            { name: "url", customName: "rendered_video_url", type: "string" },
            {
              name: "file_size",
              customName: "render_file_size",
              type: "number",
            },
          ],
        },
      }),

      // 4. Technical spec validation (runCode)
      n("4", "runCode", "Validate video technical specifications", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Validate rendered video meets hero video technical specifications
const renderStatus = vars.render_status || "";
const fileSize = parseInt(vars.render_file_size) || 0;
const duration = parseInt(vars.video_duration) || 0;
const videoUrl = vars.rendered_video_url || "";
const maxFileSizeMB = 50;
const maxDuration = 90;
const minDuration = 15;
const fileSizeMB = (fileSize / (1024 * 1024)).toFixed(2);
const issues = [];
if (renderStatus !== "succeeded") issues.push("Render did not succeed: " + renderStatus);
if (fileSize > maxFileSizeMB * 1024 * 1024) issues.push("File too large: " + fileSizeMB + "MB (max " + maxFileSizeMB + "MB)");
if (duration > maxDuration) issues.push("Too long: " + duration + "s (max " + maxDuration + "s)");
if (duration < minDuration) issues.push("Too short: " + duration + "s (min " + minDuration + "s)");
if (!videoUrl) issues.push("No video URL returned");
return {
  specs_pass: issues.length === 0 ? "yes" : "no",
  file_size_mb: fileSizeMB,
  issue_count: issues.length,
  issues_list: issues.join("; ") || "All checks passed",
  validation_summary: issues.length === 0
    ? "Video passes all specs: " + fileSizeMB + "MB, " + duration + "s"
    : issues.length + " issue(s): " + issues.join("; ")
};`,
          outputVariables: [
            { name: "specs_pass", type: "string" },
            { name: "file_size_mb", type: "string" },
            { name: "issue_count", type: "number" },
            { name: "issues_list", type: "string" },
            { name: "validation_summary", type: "string" },
          ],
        },
      }),

      // 5. Approval gate
      n("5", "approvalGate", "Approve hero video for deployment", 1000, {
        approvalGateData: {
          message:
            "Hero Video Approval\\n\\nTitle: {video_title}\\nDuration: {video_duration}s\\nFile Size: {file_size_mb}MB\\nHook: {hook_line}\\nCTA: {video_cta}\\n\\nTech Validation: {validation_summary}\\n\\nPreview: {rendered_video_url}\\n\\nNarration:\\n{narration_text}\\n\\nApprove to upload to CDN and deploy?",
        },
      }),

      // 6. Video hosting + CDN upload
      n("6", "hitApi", "Upload video to CDN", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.cloudflare.com/client/v4/accounts/{cf_account_id}/stream/copy",
          headers: [
            { key: "Authorization", value: "Bearer {cloudflare_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "url", value: "{rendered_video_url}" },
            {
              key: "meta",
              value: '{"name":"{video_title}","type":"hero_video"}',
            },
          ],
          variables: [
            { name: "result.uid", customName: "cdn_video_id", type: "string" },
            {
              name: "result.playback.hls",
              customName: "cdn_hls_url",
              type: "string",
            },
            {
              name: "result.preview",
              customName: "cdn_preview_url",
              type: "string",
            },
          ],
        },
      }),

      // 7. Deployment notification (Slack)
      slackNode(
        "7",
        "Slack: Hero video deployed notification",
        1480,
        "Hero Video Deployed\\n\\nTitle: {video_title}\\nDuration: {video_duration}s | Size: {file_size_mb}MB\\n\\nCDN URLs:\\n- HLS: {cdn_hls_url}\\n- Preview: {cdn_preview_url}\\n\\nHook: {hook_line}\\nCTA: {video_cta}\\n\\nReady to embed on landing page.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1035. LANGUAGE OR LOCALE PREFERENCE UPDATE (6 nodes)
  // ===================================================================

  "Language or Locale Preference Update": {
    summary:
      "Triggers on a preference update request, generates an AI preference request email, dispatches it, handles form responses to update the profile, syncs the preference to HubSpot, and logs fallbacks and errors to Google Sheets.",
    nodes: [
      trigger("Webhook: Locale preference update request", "webhook_app", {
        source: "internal",
        event: "preference.locale_update",
        howItStarts:
          "Fires when a customer requests to change their language or locale preference, or when the system detects a locale mismatch based on browsing behavior",
        setup:
          "Configure webhook from your customer portal or preference center. Set HubSpot API key with contacts write access. Provide SendGrid API key for preference confirmation emails.",
        inputFields: [],
      }),

      // 2. AI preference request email
      aiNode(
        "2",
        "AI: Generate locale preference email",
        280,
        "You are a multilingual customer communications specialist. Extract the customer details from the webhook payload and generate a friendly preference confirmation email. If a new locale is specified, write the email in that language. Return JSON.",
        "Process locale preference update request: {webhook_payload}. Extract customer_email, customer_name, current_locale (e.g., en-US), requested_locale (e.g., es-MX), customer_id, and timezone. Generate a preference confirmation email in the REQUESTED locale language. Return customer_email, customer_name, customer_id, current_locale, requested_locale, timezone, subject (in requested language), body_html (confirmation with link to finalize, in requested language), plain_text.",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "customer_id", as: "customer_id", type: "string" },
          { field: "current_locale", as: "current_locale", type: "string" },
          { field: "requested_locale", as: "requested_locale", type: "string" },
          { field: "timezone", as: "customer_timezone", type: "string" },
          { field: "subject", as: "pref_subject", type: "string" },
          { field: "body_html", as: "pref_body_html", type: "string" },
          { field: "plain_text", as: "pref_plain_text", type: "string" },
        ],
      ),

      // 3. Preference email dispatch
      emailNode(
        "3",
        "Send locale preference confirmation email",
        520,
        "{customer_email}",
        "{pref_subject}",
        "{pref_body_html}",
      ),

      // 4. Preference form handler (runCode)
      n("4", "runCode", "Process preference form submission", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Validate and process locale preference update
const validLocales = ['en-US','en-GB','es-ES','es-MX','fr-FR','de-DE','pt-BR','ja-JP','zh-CN','ko-KR','it-IT','nl-NL','sv-SE','da-DK','nb-NO','fi-FI','pl-PL','ru-RU','ar-SA','hi-IN'];
const requested = vars.requested_locale || "";
const current = vars.current_locale || "en-US";
const isValid = validLocales.includes(requested);
const languageName = {
  'en-US':'English (US)','en-GB':'English (UK)','es-ES':'Spanish','es-MX':'Spanish (Mexico)',
  'fr-FR':'French','de-DE':'German','pt-BR':'Portuguese (Brazil)','ja-JP':'Japanese',
  'zh-CN':'Chinese (Simplified)','ko-KR':'Korean','it-IT':'Italian','nl-NL':'Dutch'
}[requested] || requested;
return {
  locale_valid: isValid ? "yes" : "no",
  final_locale: isValid ? requested : current,
  language_name: languageName,
  changed: isValid && requested !== current ? "yes" : "no",
  update_timestamp: new Date().toISOString(),
  error_message: isValid ? "" : "Invalid locale: " + requested + ". Kept " + current
};`,
          outputVariables: [
            { name: "locale_valid", type: "string" },
            { name: "final_locale", type: "string" },
            { name: "language_name", type: "string" },
            { name: "changed", type: "string" },
            { name: "update_timestamp", type: "string" },
            { name: "error_message", type: "string" },
          ],
        },
      }),

      // 5. Profile + platform sync (HubSpot)
      n("5", "hitApi", "Sync locale preference to HubSpot", 1000, {
        hitApiDetails: {
          method: "PATCH",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/{customer_id}",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "properties",
              value:
                '{"hs_language":"{final_locale}","preferred_language":"{language_name}","locale_updated_at":"{update_timestamp}"}',
            },
          ],
          variables: [
            { name: "id", customName: "hubspot_contact_id", type: "string" },
            {
              name: "properties.hs_language",
              customName: "hubspot_locale_set",
              type: "string",
            },
          ],
        },
      }),

      // 6. Fallback + logging (Sheets)
      sheetsNode(
        "6",
        "Log locale preference update to Sheets",
        1240,
        "LocalePreferences",
        '[["{update_timestamp}","{customer_email}","{customer_id}","{current_locale}","{requested_locale}","{final_locale}","{changed}","{error_message}"]]',
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5"), e("5", "6")],
  },

  // ===================================================================
  // 1036. LAUNCH DAY SOCIAL BLITZ (8 nodes)
  // ===================================================================

  "Launch Day Social Blitz": {
    summary:
      "Triggers on launch day, fetches the content package, optimizes posts per platform with AI, formats visual assets in code, publishes in staggered batches via Buffer, monitors real-time engagement, manages community responses in Slack, and logs the day's report to Google Sheets.",
    nodes: [
      trigger("Schedule: Launch day social blitz", "manual", {
        howItStarts:
          "Manually triggered on launch day to coordinate a multi-platform social media blitz with staggered, time-optimized posting across all channels",
        setup:
          "Prepare the launch content package (messaging, assets, links) in a shared document or CMS. Configure Buffer profiles for all social channels. Set up Slack channel for real-time community management. Provide Google Sheets spreadsheet ID.",
        inputFields: [],
      }),

      // 2. Launch content package fetch
      n("2", "hitApi", "Fetch launch content package", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://docs.googleapis.com/v1/documents/{launch_content_doc_id}",
          headers: [{ key: "Authorization", value: "Bearer {google_token}" }],
          body: [],
          variables: [
            {
              name: "body.content",
              customName: "launch_content_raw",
              type: "string",
            },
            { name: "title", customName: "launch_doc_title", type: "string" },
          ],
        },
      }),

      // 3. AI platform-specific optimizer
      aiNode(
        "3",
        "AI: Optimize content for each social platform",
        520,
        "You are a social media launch strategist. Adapt launch messaging for each major platform (Twitter/X, LinkedIn, Instagram, Facebook, TikTok). Optimize character counts, tone, hashtags, and formatting for each. Include stagger timing recommendations. Return JSON.",
        "Create platform-specific launch posts from this content package: {launch_content_raw}. Return twitter_post (280 chars, punchy), linkedin_post (professional, 300 words max), instagram_caption (visual-focused, 2200 chars max with line breaks), facebook_post (conversational, shareable), tiktok_caption (Gen-Z friendly, 150 chars), hashtags_all (unified hashtag set), posting_schedule (recommended stagger: e.g., Twitter 9AM, LinkedIn 10AM, etc.), total_posts_planned (number).",
        [
          { field: "twitter_post", as: "launch_twitter", type: "string" },
          { field: "linkedin_post", as: "launch_linkedin", type: "string" },
          {
            field: "instagram_caption",
            as: "launch_instagram",
            type: "string",
          },
          { field: "facebook_post", as: "launch_facebook", type: "string" },
          { field: "tiktok_caption", as: "launch_tiktok", type: "string" },
          { field: "hashtags_all", as: "launch_hashtags", type: "string" },
          { field: "posting_schedule", as: "posting_schedule", type: "string" },
          {
            field: "total_posts_planned",
            as: "total_posts_planned",
            type: "number",
          },
        ],
      ),

      // 4. Visual asset formatter (runCode)
      n("4", "runCode", "Format visual assets for each platform", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Format launch visual assets for platform-specific dimensions
const platforms = [
  { name: "Twitter", width: 1200, height: 675, format: "jpg", post: vars.launch_twitter },
  { name: "LinkedIn", width: 1200, height: 627, format: "jpg", post: vars.launch_linkedin },
  { name: "Instagram", width: 1080, height: 1080, format: "jpg", post: vars.launch_instagram },
  { name: "Facebook", width: 1200, height: 630, format: "jpg", post: vars.launch_facebook },
  { name: "TikTok", width: 1080, height: 1920, format: "mp4", post: vars.launch_tiktok }
];
const assetManifest = platforms.map(p => ({
  platform: p.name,
  dimensions: p.width + "x" + p.height,
  format: p.format,
  text_length: (p.post || "").length,
  ready: (p.post || "").length > 0
}));
const readyCount = assetManifest.filter(a => a.ready).length;
return {
  asset_manifest_json: JSON.stringify(assetManifest),
  platforms_ready: readyCount,
  all_ready: readyCount === platforms.length ? "yes" : "no",
  launch_timestamp: new Date().toISOString(),
  asset_summary: readyCount + "/" + platforms.length + " platforms ready"
};`,
          outputVariables: [
            { name: "asset_manifest_json", type: "string" },
            { name: "platforms_ready", type: "number" },
            { name: "all_ready", type: "string" },
            { name: "launch_timestamp", type: "string" },
            { name: "asset_summary", type: "string" },
          ],
        },
      }),

      // 5. Staggered publishing (Buffer)
      bufferNode(
        "5",
        "Buffer: Publish staggered launch posts",
        1000,
        "{launch_twitter} {launch_hashtags}",
      ),

      // 6. Real-time engagement monitor
      aiNode(
        "6",
        "AI: Monitor and analyze launch engagement",
        1240,
        "You are a social media analyst monitoring a product launch in real-time. Analyze early engagement signals and recommend tactical adjustments. Return JSON.",
        "Analyze launch day engagement. Posts published: {total_posts_planned}. Schedule: {posting_schedule}. Twitter: {launch_twitter}. LinkedIn: {launch_linkedin}. Instagram: {launch_instagram}. Buffer status: {buffer_success}. Return engagement_assessment (how launch is performing), hot_platform (which platform is getting best traction), tactical_adjustment (what to do next), community_alert (any negative sentiment or issues to address), momentum_score (1-10).",
        [
          {
            field: "engagement_assessment",
            as: "engagement_assessment",
            type: "string",
          },
          { field: "hot_platform", as: "hot_platform", type: "string" },
          {
            field: "tactical_adjustment",
            as: "tactical_adjustment",
            type: "string",
          },
          { field: "community_alert", as: "community_alert", type: "string" },
          { field: "momentum_score", as: "momentum_score", type: "number" },
        ],
      ),

      // 7. Community response manager (Slack)
      slackNode(
        "7",
        "Slack: Launch day community management",
        1480,
        "LAUNCH DAY STATUS\\n\\nPlatforms Ready: {asset_summary}\\nPosting Schedule: {posting_schedule}\\n\\nEngagement: {engagement_assessment}\\nHot Platform: {hot_platform}\\n\\nTactical Adjustment: {tactical_adjustment}\\nCommunity Alert: {community_alert}\\nMomentum Score: {momentum_score}/10\\n\\nTeam: Monitor replies and engage with early adopters!",
      ),

      // 8. Launch day report (Sheets)
      sheetsNode(
        "8",
        "Log launch day report to Sheets",
        1720,
        "LaunchDayReport",
        '[["{launch_timestamp}","{total_posts_planned}","{platforms_ready}","{hot_platform}","{momentum_score}","{engagement_assessment}","{tactical_adjustment}","{community_alert}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1037. LAUNCH WEEK SOCIAL CALENDAR (8 nodes)
  // ===================================================================

  "Launch Week Social Calendar": {
    summary:
      "Triggers on launch week planning, analyzes the brief with AI, generates a 7-day content calendar, writes platform-specific posts, creates visual assets via DALL-E, gates through approval, schedules via Buffer, and tracks daily performance in Google Sheets.",
    nodes: [
      trigger("Webhook: Launch week planning brief", "webhook_app", {
        source: "internal",
        event: "launch_week.planned",
        howItStarts:
          "Fires when the marketing team submits a launch week planning brief with product details, key dates, messaging themes, and target audiences for the week-long campaign",
        setup:
          "Connect project management tool to fire webhook with launch week brief. Set OpenAI and DALL-E API keys. Configure Buffer profiles for all social channels. Provide Google Sheets spreadsheet ID.",
        inputFields: [],
      }),

      // 2. Launch brief analysis (AI)
      aiNode(
        "2",
        "AI: Analyze launch week brief",
        280,
        "You are a launch campaign strategist. Extract and structure the launch week details from the webhook payload regardless of format. Identify key moments, themes, and audiences for each day. Return JSON.",
        "Extract launch week brief: {webhook_payload}. Return product_name, launch_date, week_theme, target_audiences (comma-separated), key_features (comma-separated), day1_theme through day7_theme (one theme per day), brand_voice, campaign_hashtag, competitor_context.",
        [
          { field: "product_name", as: "product_name", type: "string" },
          { field: "launch_date", as: "launch_date", type: "string" },
          { field: "week_theme", as: "week_theme", type: "string" },
          { field: "target_audiences", as: "target_audiences", type: "string" },
          { field: "key_features", as: "key_features", type: "string" },
          { field: "day1_theme", as: "day1_theme", type: "string" },
          { field: "day7_theme", as: "day7_theme", type: "string" },
          { field: "brand_voice", as: "brand_voice", type: "string" },
          { field: "campaign_hashtag", as: "campaign_hashtag", type: "string" },
        ],
      ),

      // 3. AI 7-day content calendar
      aiNode(
        "3",
        "AI: Generate 7-day content calendar",
        520,
        "You are a content calendar strategist. Create a detailed 7-day social media content calendar for a product launch week. Each day should build momentum toward launch and have a clear theme, post types, and optimal posting times. Return JSON.",
        "Create 7-day content calendar for {product_name} launch. Week theme: {week_theme}. Audiences: {target_audiences}. Features: {key_features}. Voice: {brand_voice}. Hashtag: {campaign_hashtag}. Day 1 theme: {day1_theme}. Day 7 theme: {day7_theme}. Return calendar_json (array of 7 objects: day_number, theme, post_count, platforms, timing, content_type), total_posts (number), content_mix (breakdown of types), momentum_strategy (how excitement builds).",
        [
          { field: "calendar_json", as: "calendar_json", type: "string" },
          { field: "total_posts", as: "total_week_posts", type: "number" },
          { field: "content_mix", as: "content_mix", type: "string" },
          {
            field: "momentum_strategy",
            as: "momentum_strategy",
            type: "string",
          },
        ],
      ),

      // 4. Platform-specific content writer
      aiNode(
        "4",
        "AI: Write platform-specific launch posts",
        760,
        "You are a multi-platform social media copywriter for product launches. Write the first day's posts across all platforms following the calendar plan. Each post should match platform conventions. Return JSON.",
        "Write Day 1 posts for {product_name} launch. Theme: {day1_theme}. Calendar: {calendar_json}. Hashtag: {campaign_hashtag}. Voice: {brand_voice}. Return twitter_post (280 chars), linkedin_post (professional, 200 words), instagram_caption (visual-first), facebook_post (conversational), stories_script (for Instagram/Facebook stories), engagement_hooks (questions/polls to drive interaction).",
        [
          { field: "twitter_post", as: "day1_twitter", type: "string" },
          { field: "linkedin_post", as: "day1_linkedin", type: "string" },
          { field: "instagram_caption", as: "day1_instagram", type: "string" },
          { field: "facebook_post", as: "day1_facebook", type: "string" },
          { field: "stories_script", as: "day1_stories", type: "string" },
          { field: "engagement_hooks", as: "day1_hooks", type: "string" },
        ],
      ),

      // 5. Visual asset planner (DALL-E)
      n("5", "hitApi", "Generate visual assets via DALL-E", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.openai.com/v1/images/generations",
          headers: [
            { key: "Authorization", value: "Bearer {openai_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "model", value: "dall-e-3" },
            {
              key: "prompt",
              value:
                "Professional product launch social media graphic for {product_name}. Theme: {day1_theme}. Style: modern, clean, eye-catching. Brand voice: {brand_voice}. Include visual representation of key feature. No text overlay. High contrast colors suitable for social media feeds.",
            },
            { key: "size", value: "1024x1024" },
            { key: "quality", value: "standard" },
          ],
          variables: [
            {
              name: "data[0].url",
              customName: "day1_image_url",
              type: "string",
            },
            {
              name: "data[0].revised_prompt",
              customName: "revised_image_prompt",
              type: "string",
            },
          ],
        },
      }),

      // 6. Calendar review approval gate
      n(
        "6",
        "approvalGate",
        "Approve launch week calendar and Day 1 content",
        1240,
        {
          approvalGateData: {
            message:
              "Launch Week Calendar Review\\n\\nProduct: {product_name}\\nLaunch Date: {launch_date}\\nWeek Theme: {week_theme}\\nTotal Posts Planned: {total_week_posts}\\nContent Mix: {content_mix}\\n\\nMomentum Strategy: {momentum_strategy}\\n\\nDay 1 Preview:\\n- Twitter: {day1_twitter}\\n- LinkedIn: {day1_linkedin}\\n- Instagram: {day1_instagram}\\n- Stories: {day1_stories}\\n\\nImage: {day1_image_url}\\n\\nApprove to schedule Day 1 posts?",
          },
        },
      ),

      // 7. Scheduled publisher (Buffer)
      bufferNode(
        "7",
        "Buffer: Schedule Day 1 launch posts",
        1480,
        "{day1_twitter} {campaign_hashtag}",
      ),

      // 8. Daily performance tracker (Sheets)
      sheetsNode(
        "8",
        "Log launch week plan to Sheets",
        1720,
        "LaunchWeekCalendar",
        '[["{launch_date}","{product_name}","{week_theme}","Day 1","{day1_theme}","{total_week_posts}","{content_mix}","{momentum_strategy}","{day1_image_url}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1038. LEAD FORM AD CAMPAIGN (8 nodes)
  // ===================================================================

  "Lead Form Ad Campaign": {
    summary:
      "Triggers on a campaign brief, uses AI to generate form questions and ad copy, creates a Meta lead form and campaign with ad sets, handles lead webhooks, routes leads to HubSpot CRM, and monitors errors in Google Sheets.",
    nodes: [
      trigger("Webhook: Lead form campaign brief", "webhook_app", {
        source: "internal",
        event: "lead_campaign.requested",
        howItStarts:
          "Fires when a marketer submits a lead generation campaign brief including target audience, offer details, budget, and qualifying criteria for Meta lead form ads",
        setup:
          "Configure webhook from your campaign planning tool. Set Meta Marketing API access token with ads_management permission. Provide HubSpot API key for lead routing. Set Google Sheets spreadsheet ID for monitoring.",
        inputFields: [],
      }),

      // 2. AI form question generation
      aiNode(
        "2",
        "AI: Generate lead form questions",
        280,
        "You are a lead generation specialist. Extract the campaign brief and design lead form questions that qualify prospects without creating too much friction. Balance information capture with conversion rate. Return JSON.",
        "Design lead form from brief: {webhook_payload}. Return campaign_name, target_audience, offer_description, budget_daily (number), form_questions_json (array of {label, type: TEXT|SELECT|CONDITIONAL, options: string[], required: boolean}, max 5 questions), qualifying_threshold (score that indicates a qualified lead), privacy_policy_url, thank_you_message.",
        [
          { field: "campaign_name", as: "campaign_name", type: "string" },
          { field: "target_audience", as: "target_audience", type: "string" },
          {
            field: "offer_description",
            as: "offer_description",
            type: "string",
          },
          { field: "budget_daily", as: "budget_daily", type: "number" },
          {
            field: "form_questions_json",
            as: "form_questions_json",
            type: "string",
          },
          {
            field: "qualifying_threshold",
            as: "qualifying_threshold",
            type: "number",
          },
          {
            field: "privacy_policy_url",
            as: "privacy_policy_url",
            type: "string",
          },
          {
            field: "thank_you_message",
            as: "thank_you_message",
            type: "string",
          },
        ],
      ),

      // 3. AI ad copy generation
      aiNode(
        "3",
        "AI: Generate ad copy for lead form campaign",
        520,
        "You are a Meta Ads copywriter specializing in lead generation. Write high-converting ad copy that drives form submissions. Use proven direct-response techniques. Return JSON.",
        "Write Meta lead form ad copy. Campaign: {campaign_name}. Audience: {target_audience}. Offer: {offer_description}. Return primary_text (125 chars, hook-driven), headline (40 chars, value prop), description (30 chars, supporting), link_description (20 chars, CTA context), ad_creative_concept (visual direction), cta_button (LEARN_MORE, SIGN_UP, SUBSCRIBE, GET_OFFER, or APPLY_NOW).",
        [
          { field: "primary_text", as: "ad_primary_text", type: "string" },
          { field: "headline", as: "ad_headline", type: "string" },
          { field: "description", as: "ad_description", type: "string" },
          { field: "link_description", as: "ad_link_desc", type: "string" },
          {
            field: "ad_creative_concept",
            as: "ad_creative_concept",
            type: "string",
          },
          { field: "cta_button", as: "ad_cta_button", type: "string" },
        ],
      ),

      // 4. Meta lead form creation
      n("4", "hitApi", "Create Meta lead generation form", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://graph.facebook.com/v18.0/{meta_page_id}/leadgen_forms",
          headers: [
            { key: "Authorization", value: "Bearer {meta_access_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "name", value: "Lead Form: {campaign_name}" },
            { key: "questions", value: "{form_questions_json}" },
            { key: "privacy_policy", value: '{"url":"{privacy_policy_url}"}' },
            {
              key: "thank_you_page",
              value: '{"title":"Thank You!","body":"{thank_you_message}"}',
            },
            { key: "follow_up_action_url", value: "{company_url}" },
          ],
          variables: [
            { name: "id", customName: "lead_form_id", type: "string" },
            { name: "status", customName: "form_status", type: "string" },
          ],
        },
      }),

      // 5. Campaign + ad set builder
      n("5", "hitApi", "Create Meta campaign and ad set", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://graph.facebook.com/v18.0/act_{meta_ad_account_id}/campaigns",
          headers: [
            { key: "Authorization", value: "Bearer {meta_access_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "name", value: "{campaign_name}" },
            { key: "objective", value: "OUTCOME_LEADS" },
            { key: "status", value: "PAUSED" },
            { key: "special_ad_categories", value: "[]" },
            { key: "daily_budget", value: "{budget_daily}00" },
          ],
          variables: [
            { name: "id", customName: "meta_campaign_id", type: "string" },
          ],
        },
      }),

      // 6. Lead webhook handler (runCode)
      n("6", "runCode", "Configure lead webhook and validate setup", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Validate campaign setup and prepare lead routing configuration
const campaignId = vars.meta_campaign_id || "";
const formId = vars.lead_form_id || "";
const formStatus = vars.form_status || "";
const campaignName = vars.campaign_name || "";
const threshold = parseInt(vars.qualifying_threshold) || 50;
const issues = [];
if (!campaignId) issues.push("Campaign creation failed");
if (!formId) issues.push("Lead form creation failed");
if (formStatus !== "ACTIVE" && formStatus !== "active") issues.push("Form status: " + formStatus);
return {
  setup_valid: issues.length === 0 ? "yes" : "no",
  campaign_id_out: campaignId,
  form_id_out: formId,
  webhook_url: "{workflow_base_url}/webhooks/meta-leads/" + campaignId,
  qualifying_score: String(threshold),
  setup_issues: issues.join("; ") || "All systems configured",
  routing_config: JSON.stringify({
    hubspot_pipeline: "{hubspot_pipeline_id}",
    hubspot_stage: "qualifiedtobuy",
    notification_email: "{admin_email}",
    auto_assign: true
  }),
  setup_summary: issues.length === 0
    ? "Campaign " + campaignName + " ready. Form: " + formId + ". Leads route to HubSpot."
    : issues.length + " setup issue(s): " + issues.join("; ")
};`,
          outputVariables: [
            { name: "setup_valid", type: "string" },
            { name: "campaign_id_out", type: "string" },
            { name: "form_id_out", type: "string" },
            { name: "webhook_url", type: "string" },
            { name: "qualifying_score", type: "string" },
            { name: "setup_issues", type: "string" },
            { name: "routing_config", type: "string" },
            { name: "setup_summary", type: "string" },
          ],
        },
      }),

      // 7. CRM lead router (HubSpot)
      n("7", "hitApi", "Create lead contact in HubSpot CRM", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/contacts",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "properties",
              value:
                '{"email":"{lead_email}","firstname":"{lead_first_name}","lastname":"{lead_last_name}","lifecyclestage":"lead","hs_lead_status":"NEW","lead_source":"Meta Lead Form","lead_form_campaign":"{campaign_name}","lead_form_id":"{form_id_out}"}',
            },
          ],
          variables: [
            { name: "id", customName: "hubspot_lead_id", type: "string" },
            {
              name: "properties.email",
              customName: "hubspot_lead_email",
              type: "string",
            },
          ],
        },
      }),

      // 8. Campaign monitor + error handler (Sheets)
      sheetsNode(
        "8",
        "Log campaign setup and monitor to Sheets",
        1720,
        "LeadFormCampaigns",
        '[["{campaign_name}","{meta_campaign_id}","{lead_form_id}","{form_status}","{budget_daily}","{target_audience}","{setup_valid}","{setup_issues}","{ad_headline}","{ad_cta_button}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1039. LIFECYCLE EMAIL STRATEGY (8 nodes)
  // ===================================================================

  "Lifecycle Email Strategy": {
    summary:
      "Triggers on strategy input, maps the customer journey with AI, generates stage-specific strategies for awareness, consideration, conversion, and retention/advocacy, builds a comprehensive strategy document in Google Docs, and delivers via email and Slack.",
    nodes: [
      trigger("Webhook: Lifecycle email strategy request", "webhook_app", {
        source: "internal",
        event: "strategy.lifecycle_email",
        howItStarts:
          "Fires when a marketing leader requests a comprehensive lifecycle email strategy, providing business context, customer segments, and current email performance data",
        setup:
          "Configure webhook from your strategy planning tool. Set OpenAI API key for AI strategy generation. Provide Google Docs and Slack credentials for delivery.",
        inputFields: [],
      }),

      // 2. Customer journey mapping (AI)
      aiNode(
        "2",
        "AI: Map customer journey and extract brief",
        280,
        "You are a customer journey strategist. Extract the business context from the webhook payload and map the complete customer lifecycle with key touchpoints, emotions, and email opportunities at each stage. Return JSON.",
        "Map the customer lifecycle from this brief: {webhook_payload}. Return business_name, industry, customer_segments (comma-separated), current_email_count (approximate monthly sends), journey_stages_json (array of {stage, touchpoints, customer_emotion, email_opportunity, key_metric}), biggest_gap (where current strategy falls short), strategy_owner_email.",
        [
          { field: "business_name", as: "business_name", type: "string" },
          { field: "industry", as: "industry", type: "string" },
          {
            field: "customer_segments",
            as: "customer_segments",
            type: "string",
          },
          {
            field: "current_email_count",
            as: "current_email_count",
            type: "number",
          },
          {
            field: "journey_stages_json",
            as: "journey_stages_json",
            type: "string",
          },
          { field: "biggest_gap", as: "biggest_gap", type: "string" },
          {
            field: "strategy_owner_email",
            as: "strategy_owner_email",
            type: "string",
          },
        ],
      ),

      // 3. AI awareness stage strategy
      aiNode(
        "3",
        "AI: Awareness stage email strategy",
        520,
        "You are an email marketing strategist. Design the awareness stage email strategy including welcome sequences, lead magnets, educational drips, and brand introduction series. Return JSON.",
        "Design awareness stage email strategy for {business_name} ({industry}). Segments: {customer_segments}. Journey context: {journey_stages_json}. Gap: {biggest_gap}. Return awareness_flows (array of flow names and descriptions), welcome_sequence_emails (number), lead_magnet_ideas (3 ideas), drip_cadence (frequency recommendation), awareness_kpis (key metrics to track), awareness_summary (2-3 sentence overview).",
        [
          { field: "awareness_flows", as: "awareness_flows", type: "string" },
          {
            field: "welcome_sequence_emails",
            as: "welcome_email_count",
            type: "number",
          },
          {
            field: "lead_magnet_ideas",
            as: "lead_magnet_ideas",
            type: "string",
          },
          { field: "drip_cadence", as: "awareness_cadence", type: "string" },
          { field: "awareness_kpis", as: "awareness_kpis", type: "string" },
          {
            field: "awareness_summary",
            as: "awareness_summary",
            type: "string",
          },
        ],
      ),

      // 4. AI consideration stage strategy
      aiNode(
        "4",
        "AI: Consideration stage email strategy",
        760,
        "You are an email marketing strategist. Design the consideration stage email strategy including nurture sequences, case studies, comparison guides, and product education. Return JSON.",
        "Design consideration stage email strategy for {business_name} ({industry}). Segments: {customer_segments}. Return consideration_flows (array of flow names), nurture_sequence_length (number of emails), content_types (case studies, webinars, etc.), personalization_strategy (how to segment and personalize), consideration_kpis, consideration_summary.",
        [
          {
            field: "consideration_flows",
            as: "consideration_flows",
            type: "string",
          },
          {
            field: "nurture_sequence_length",
            as: "nurture_email_count",
            type: "number",
          },
          {
            field: "content_types",
            as: "consideration_content",
            type: "string",
          },
          {
            field: "personalization_strategy",
            as: "personalization_strategy",
            type: "string",
          },
          {
            field: "consideration_kpis",
            as: "consideration_kpis",
            type: "string",
          },
          {
            field: "consideration_summary",
            as: "consideration_summary",
            type: "string",
          },
        ],
      ),

      // 5. AI conversion stage strategy
      aiNode(
        "5",
        "AI: Conversion stage email strategy",
        1000,
        "You are an email marketing strategist focused on conversion optimization. Design the conversion stage strategy including cart abandonment, trial conversion, urgency sequences, and social proof triggers. Return JSON.",
        "Design conversion stage email strategy for {business_name} ({industry}). Segments: {customer_segments}. Return conversion_flows (array of flow names), abandonment_sequence (email count and timing), trial_conversion_strategy, urgency_techniques (3 approaches), conversion_kpis, conversion_summary.",
        [
          { field: "conversion_flows", as: "conversion_flows", type: "string" },
          {
            field: "abandonment_sequence",
            as: "abandonment_strategy",
            type: "string",
          },
          {
            field: "trial_conversion_strategy",
            as: "trial_strategy",
            type: "string",
          },
          {
            field: "urgency_techniques",
            as: "urgency_techniques",
            type: "string",
          },
          { field: "conversion_kpis", as: "conversion_kpis", type: "string" },
          {
            field: "conversion_summary",
            as: "conversion_summary",
            type: "string",
          },
        ],
      ),

      // 6. AI retention + advocacy strategy
      aiNode(
        "6",
        "AI: Retention and advocacy stage strategy",
        1240,
        "You are an email marketing strategist focused on customer retention and advocacy. Design post-purchase nurture, loyalty programs, referral campaigns, review requests, and win-back flows. Return JSON.",
        "Design retention and advocacy email strategy for {business_name} ({industry}). Segments: {customer_segments}. Return retention_flows (array of flow names), loyalty_program_emails (count), referral_campaign_design, review_request_timing, winback_triggers, retention_kpis, advocacy_summary, total_strategy_emails (total emails across all lifecycle stages).",
        [
          { field: "retention_flows", as: "retention_flows", type: "string" },
          {
            field: "loyalty_program_emails",
            as: "loyalty_email_count",
            type: "number",
          },
          {
            field: "referral_campaign_design",
            as: "referral_design",
            type: "string",
          },
          {
            field: "review_request_timing",
            as: "review_timing",
            type: "string",
          },
          { field: "winback_triggers", as: "winback_triggers", type: "string" },
          { field: "retention_kpis", as: "retention_kpis", type: "string" },
          { field: "advocacy_summary", as: "advocacy_summary", type: "string" },
          {
            field: "total_strategy_emails",
            as: "total_strategy_emails",
            type: "number",
          },
        ],
      ),

      // 7. Strategy document builder (Google Docs)
      n("7", "hitApi", "Save lifecycle strategy to Google Docs", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://docs.googleapis.com/v1/documents",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "title",
              value: "Lifecycle Email Strategy: {business_name}",
            },
            {
              key: "body",
              value:
                '{"content":[{"paragraph":{"elements":[{"textRun":{"content":"LIFECYCLE EMAIL STRATEGY — {business_name}\\n\\nIndustry: {industry}\\nSegments: {customer_segments}\\nTotal Emails: {total_strategy_emails}\\n\\n=== AWARENESS STAGE ===\\n{awareness_summary}\\nFlows: {awareness_flows}\\nKPIs: {awareness_kpis}\\n\\n=== CONSIDERATION STAGE ===\\n{consideration_summary}\\nFlows: {consideration_flows}\\nKPIs: {consideration_kpis}\\n\\n=== CONVERSION STAGE ===\\n{conversion_summary}\\nFlows: {conversion_flows}\\nKPIs: {conversion_kpis}\\n\\n=== RETENTION & ADVOCACY ===\\n{advocacy_summary}\\nFlows: {retention_flows}\\nKPIs: {retention_kpis}"}}]}}]}',
            },
          ],
          variables: [
            {
              name: "documentId",
              customName: "strategy_doc_id",
              type: "string",
            },
            {
              name: "documentUri",
              customName: "strategy_doc_url",
              type: "string",
            },
          ],
        },
      }),

      // 8. Delivery + workshop setup (email + Slack)
      emailNode(
        "8",
        "Deliver strategy document and schedule workshop",
        1720,
        "{strategy_owner_email}",
        "Your Lifecycle Email Strategy is Ready: {business_name}",
        "Hi,\\n\\nYour comprehensive lifecycle email strategy for {business_name} is ready for review.\\n\\nDocument: {strategy_doc_url}\\n\\nHighlights:\\n- Total emails across lifecycle: {total_strategy_emails}\\n- Awareness: {awareness_summary}\\n- Consideration: {consideration_summary}\\n- Conversion: {conversion_summary}\\n- Retention: {advocacy_summary}\\n\\nBiggest gap addressed: {biggest_gap}\\n\\nPlease review and schedule a workshop session to align the team on implementation priorities.\\n\\nBest regards,\\n{company_name} Strategy Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1040. LINK IN BIO PAGE UPDATER (7 nodes)
  // ===================================================================

  "Link in Bio Page Updater": {
    summary:
      "Triggers on a content update request, ranks content priorities in code, generates AI link descriptions, builds the link-in-bio page structure, updates thumbnails and visuals in code, publishes with cache clear, and logs the update confirmation to Google Sheets.",
    nodes: [
      trigger("Webhook: Link-in-bio content update", "webhook_app", {
        source: "internal",
        event: "linkinbio.update",
        howItStarts:
          "Fires when new content is published (blog post, video, product launch, etc.) that should be added to the link-in-bio page, or when a manual reorder is requested",
        setup:
          "Configure webhooks from your CMS, YouTube, or product management tool. Set API credentials for your link-in-bio service (Linktree, Later, or custom). Provide Google Sheets spreadsheet ID for tracking.",
        inputFields: [],
      }),

      // 2. Content priority ranker (runCode)
      n("2", "runCode", "Rank and prioritize link content", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse webhook payload and rank links by priority
let payload;
try { payload = JSON.parse(vars.webhook_payload || "{}"); } catch(e) { payload = { title: vars.webhook_payload }; }
const newLink = {
  title: payload.title || payload.name || "New Content",
  url: payload.url || payload.link || "",
  type: payload.type || payload.content_type || "article",
  date: payload.published_at || new Date().toISOString(),
  priority: payload.priority || "medium"
};
const priorityWeights = { urgent: 100, high: 75, medium: 50, low: 25 };
const typeWeights = { product: 90, launch: 85, video: 70, article: 50, podcast: 45, social: 30 };
const recencyBonus = Math.max(0, 30 - Math.floor((Date.now() - new Date(newLink.date).getTime()) / 86400000));
const score = (priorityWeights[newLink.priority] || 50) + (typeWeights[newLink.type] || 40) + recencyBonus;
return {
  link_title: newLink.title,
  link_url: newLink.url,
  link_type: newLink.type,
  link_priority: newLink.priority,
  priority_score: score,
  published_date: newLink.date,
  needs_thumbnail: newLink.type === "video" || newLink.type === "product" ? "yes" : "no",
  update_action: "add_to_top"
};`,
          outputVariables: [
            { name: "link_title", type: "string" },
            { name: "link_url", type: "string" },
            { name: "link_type", type: "string" },
            { name: "link_priority", type: "string" },
            { name: "priority_score", type: "number" },
            { name: "published_date", type: "string" },
            { name: "needs_thumbnail", type: "string" },
            { name: "update_action", type: "string" },
          ],
        },
      }),

      // 3. AI link description writer
      aiNode(
        "3",
        "AI: Write compelling link descriptions",
        520,
        "You are a copywriter specializing in short-form link descriptions for link-in-bio pages. Write concise, action-driving descriptions that make people want to click. Each description should be under 60 characters. Return JSON.",
        "Write a link-in-bio description for: Title: {link_title}. URL: {link_url}. Type: {link_type}. Return short_description (under 60 chars, action-oriented), emoji_prefix (single relevant emoji), cta_text (2-3 word CTA), category_tag (for grouping: Featured, Latest, Shop, Watch, Read, Listen).",
        [
          {
            field: "short_description",
            as: "link_description",
            type: "string",
          },
          { field: "emoji_prefix", as: "link_emoji", type: "string" },
          { field: "cta_text", as: "link_cta", type: "string" },
          { field: "category_tag", as: "link_category", type: "string" },
        ],
      ),

      // 4. Link-in-bio page builder
      n("4", "hitApi", "Update link-in-bio page via API", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.linktree.com/api/v1/links",
          headers: [
            { key: "Authorization", value: "Bearer {linktree_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "title", value: "{link_emoji} {link_title}" },
            { key: "url", value: "{link_url}" },
            { key: "position", value: "0" },
            { key: "thumbnail", value: "{thumbnail_url}" },
          ],
          variables: [
            { name: "id", customName: "linktree_link_id", type: "string" },
            { name: "position", customName: "link_position", type: "number" },
          ],
        },
      }),

      // 5. Thumbnail + visual updater (runCode)
      n("5", "runCode", "Generate thumbnail metadata and validate", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Validate link creation and prepare thumbnail metadata
const linkId = vars.linktree_link_id || "";
const linkTitle = vars.link_title || "";
const linkUrl = vars.link_url || "";
const needsThumbnail = vars.needs_thumbnail === "yes";
const linkType = vars.link_type || "article";
const thumbnailSpecs = {
  video: { width: 400, height: 225, format: "jpg", source: "video_frame" },
  product: { width: 400, height: 400, format: "jpg", source: "product_image" },
  article: { width: 400, height: 210, format: "jpg", source: "og_image" },
  default: { width: 400, height: 225, format: "jpg", source: "auto" }
};
const spec = thumbnailSpecs[linkType] || thumbnailSpecs.default;
return {
  link_created: linkId ? "yes" : "no",
  thumbnail_spec: JSON.stringify(spec),
  thumbnail_needed: needsThumbnail ? "yes" : "no",
  total_active_links: "updated",
  page_cache_key: "linkinbio_" + Date.now(),
  update_summary: linkId
    ? "Added '" + linkTitle + "' at position 0. Type: " + linkType + "."
    : "Failed to create link for: " + linkTitle
};`,
          outputVariables: [
            { name: "link_created", type: "string" },
            { name: "thumbnail_spec", type: "string" },
            { name: "thumbnail_needed", type: "string" },
            { name: "total_active_links", type: "string" },
            { name: "page_cache_key", type: "string" },
            { name: "update_summary", type: "string" },
          ],
        },
      }),

      // 6. Publication + cache clear
      n("6", "hitApi", "Clear CDN cache for link-in-bio page", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.cloudflare.com/client/v4/zones/{cf_zone_id}/purge_cache",
          headers: [
            { key: "Authorization", value: "Bearer {cloudflare_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [{ key: "files", value: '["{linkinbio_page_url}"]' }],
          variables: [
            { name: "success", customName: "cache_cleared", type: "boolean" },
          ],
        },
      }),

      // 7. Update confirmation + monitor (Sheets)
      sheetsNode(
        "7",
        "Log link-in-bio update to Sheets",
        1480,
        "LinkInBioUpdates",
        '[["{published_date}","{link_title}","{link_url}","{link_type}","{link_category}","{link_description}","{priority_score}","{link_created}","{cache_cleared}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1041. LINKEDIN CONTENT CALENDAR POST (7 nodes)
  // ===================================================================

  "LinkedIn Content Calendar Post": {
    summary:
      "Triggers on a content calendar schedule, fetches the topic and brief, generates AI thought leadership copy, optimizes formatting and hashtags in code, gates through approval, publishes to LinkedIn, and tracks performance in Google Sheets.",
    nodes: [
      trigger("Schedule: LinkedIn content calendar post", "schedule", {
        schedule: "0 8 * * 2,4 (Tue & Thu at 8 AM)",
        howItStarts:
          "A scheduled job triggers the creation and publishing of thought leadership content on LinkedIn based on the pre-planned content calendar",
        setup:
          "Maintain a content calendar in Google Sheets with topics, briefs, and target dates. Configure LinkedIn API credentials with w_member_social scope. Provide Google Sheets spreadsheet ID for performance tracking.",
        inputFields: [],
      }),

      // 2. Topic + brief fetch
      n("2", "hitApi", "Fetch next topic from content calendar", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/ContentCalendar!A2:F2",
          headers: [{ key: "Authorization", value: "Bearer {google_token}" }],
          body: [],
          variables: [
            { name: "values[0][0]", customName: "post_topic", type: "string" },
            { name: "values[0][1]", customName: "post_brief", type: "string" },
            {
              name: "values[0][2]",
              customName: "content_pillar",
              type: "string",
            },
            {
              name: "values[0][3]",
              customName: "target_audience",
              type: "string",
            },
            { name: "values[0][4]", customName: "post_format", type: "string" },
            {
              name: "values[0][5]",
              customName: "reference_url",
              type: "string",
            },
          ],
        },
      }),

      // 3. AI thought leadership post
      aiNode(
        "3",
        "AI: Write LinkedIn thought leadership post",
        520,
        "You are a LinkedIn thought leadership ghostwriter. Write posts that combine personal insight with professional expertise. Use short paragraphs, line breaks for readability, and end with an engaging question or CTA. Avoid corporate jargon. Return JSON.",
        "Write a LinkedIn post. Topic: {post_topic}. Brief: {post_brief}. Pillar: {content_pillar}. Audience: {target_audience}. Format: {post_format}. Reference: {reference_url}. Return post_text (1300 chars max, formatted with line breaks), hook_line (first line that stops the scroll), closing_question (engagement driver), key_takeaway (one-sentence summary), content_angle (unique perspective used).",
        [
          { field: "post_text", as: "linkedin_post_text", type: "string" },
          { field: "hook_line", as: "hook_line", type: "string" },
          { field: "closing_question", as: "closing_question", type: "string" },
          { field: "key_takeaway", as: "key_takeaway", type: "string" },
          { field: "content_angle", as: "content_angle", type: "string" },
        ],
      ),

      // 4. Formatting + hashtag optimizer (runCode)
      n("4", "runCode", "Optimize formatting and generate hashtags", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Optimize LinkedIn post formatting and add hashtags
const postText = vars.linkedin_post_text || "";
const topic = vars.post_topic || "";
const pillar = vars.content_pillar || "";
// Generate relevant hashtags based on topic and pillar
const topicWords = topic.toLowerCase().split(/\\s+/).filter(w => w.length > 4);
const hashtags = topicWords.slice(0, 3).map(w => "#" + w.charAt(0).toUpperCase() + w.slice(1));
if (pillar) hashtags.push("#" + pillar.replace(/\\s+/g, ""));
hashtags.push("#ThoughtLeadership");
const uniqueHashtags = [...new Set(hashtags)].slice(0, 5);
// Format check
const charCount = postText.length;
const lineBreaks = (postText.match(/\\n/g) || []).length;
const isWellFormatted = charCount <= 1300 && lineBreaks >= 3;
const formattedPost = postText + "\\n\\n" + uniqueHashtags.join(" ");
return {
  formatted_post: formattedPost,
  hashtags_added: uniqueHashtags.join(" "),
  char_count: String(charCount),
  line_break_count: String(lineBreaks),
  well_formatted: isWellFormatted ? "yes" : "no",
  post_ready: charCount > 0 ? "yes" : "no"
};`,
          outputVariables: [
            { name: "formatted_post", type: "string" },
            { name: "hashtags_added", type: "string" },
            { name: "char_count", type: "string" },
            { name: "line_break_count", type: "string" },
            { name: "well_formatted", type: "string" },
            { name: "post_ready", type: "string" },
          ],
        },
      }),

      // 5. Approval gate
      n("5", "approvalGate", "Approve LinkedIn post for publishing", 1000, {
        approvalGateData: {
          message:
            "LinkedIn Post Review\\n\\nTopic: {post_topic}\\nPillar: {content_pillar}\\nAudience: {target_audience}\\n\\nPost Preview:\\n{formatted_post}\\n\\nHook: {hook_line}\\nClosing Q: {closing_question}\\nAngle: {content_angle}\\n\\nStats: {char_count} chars, {line_break_count} line breaks\\nHashtags: {hashtags_added}\\n\\nApprove to publish?",
        },
      }),

      // 6. LinkedIn publisher
      n("6", "hitApi", "Publish post to LinkedIn", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.linkedin.com/v2/ugcPosts",
          headers: [
            { key: "Authorization", value: "Bearer {linkedin_token}" },
            { key: "Content-Type", value: "application/json" },
            { key: "X-Restli-Protocol-Version", value: "2.0.0" },
          ],
          body: [
            { key: "author", value: "urn:li:person:{linkedin_person_id}" },
            { key: "lifecycleState", value: "PUBLISHED" },
            {
              key: "specificContent",
              value:
                '{"com.linkedin.ugc.ShareContent":{"shareCommentary":{"text":"{formatted_post}"},"shareMediaCategory":"NONE"}}',
            },
            {
              key: "visibility",
              value: '{"com.linkedin.ugc.MemberNetworkVisibility":"PUBLIC"}',
            },
          ],
          variables: [
            { name: "id", customName: "linkedin_post_id", type: "string" },
          ],
        },
      }),

      // 7. Performance tracker (Sheets)
      sheetsNode(
        "7",
        "Log LinkedIn post performance to Sheets",
        1480,
        "LinkedInPerformance",
        '[["{post_topic}","{content_pillar}","{target_audience}","{content_angle}","{char_count}","{hashtags_added}","{linkedin_post_id}","{key_takeaway}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1042. LINKEDIN VIDEO POST (7 nodes)
  // ===================================================================

  "LinkedIn Video Post": {
    summary:
      "Triggers on a video brief, generates an AI video script, renders the video via Creatomate, checks quality and professional standards with a condition, gates through approval, publishes to LinkedIn, and monitors video performance in Google Sheets.",
    nodes: [
      trigger("Webhook: LinkedIn video post brief", "webhook_app", {
        source: "internal",
        event: "linkedin_video.requested",
        howItStarts:
          "Fires when a team member submits a LinkedIn video brief including topic, key points, target audience, and desired style for professional video content",
        setup:
          "Configure webhook from your content planning tool. Set Creatomate API key and LinkedIn API credentials with w_member_social scope. Provide Google Sheets spreadsheet ID for performance monitoring.",
        inputFields: [],
      }),

      // 2. AI video script
      aiNode(
        "2",
        "AI: Generate LinkedIn video script",
        280,
        "You are a LinkedIn video script writer. Write scripts for professional, educational videos (60-120 seconds) that establish authority. Include a hook in the first 3 seconds, structured key points, and a clear CTA. Return JSON.",
        "Write a LinkedIn video script from this brief: {webhook_payload}. Return video_title, speaker_name (extract or use Company Representative), hook_opening (first 3-second hook), script_body (structured with timing markers, conversational tone), key_points (3-5 bullet points covered), cta_closing (professional CTA), total_duration_seconds (number), background_style (professional office, whiteboard, slides, etc.).",
        [
          { field: "video_title", as: "video_title", type: "string" },
          { field: "speaker_name", as: "speaker_name", type: "string" },
          { field: "hook_opening", as: "hook_opening", type: "string" },
          { field: "script_body", as: "script_body", type: "string" },
          { field: "key_points", as: "key_points", type: "string" },
          { field: "cta_closing", as: "cta_closing", type: "string" },
          {
            field: "total_duration_seconds",
            as: "video_duration",
            type: "number",
          },
          { field: "background_style", as: "background_style", type: "string" },
        ],
      ),

      // 3. Video rendering (Creatomate)
      n("3", "hitApi", "Render LinkedIn video via Creatomate", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.creatomate.com/v1/renders",
          headers: [
            { key: "Authorization", value: "Bearer {creatomate_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "template_id", value: "{creatomate_linkedin_template_id}" },
            {
              key: "modifications",
              value:
                '{"title":"{video_title}","speaker":"{speaker_name}","hook":"{hook_opening}","script":"{script_body}","cta":"{cta_closing}","background":"{background_style}","key_points":"{key_points}"}',
            },
          ],
          variables: [
            { name: "id", customName: "render_id", type: "string" },
            { name: "status", customName: "render_status", type: "string" },
            { name: "url", customName: "rendered_video_url", type: "string" },
            {
              name: "file_size",
              customName: "render_file_size",
              type: "number",
            },
          ],
        },
      }),

      // 4. Quality + professional standards check condition
      n("4", "condition", "Does video meet professional standards?", 760, {
        conditionData: [
          {
            variable: "render_status",
            value: "succeeded",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
          {
            variable: "video_duration",
            value: "120",
            comparison_operator: "LESS THAN OR EQUAL TO",
            logical_operator: "END",
          },
        ],
      }),

      // 5. Approval gate (True path)
      n("5", "approvalGate", "Approve LinkedIn video for publishing", 1000, {
        approvalGateData: {
          message:
            "LinkedIn Video Review\\n\\nTitle: {video_title}\\nSpeaker: {speaker_name}\\nDuration: {video_duration}s\\nRender Status: {render_status}\\n\\nHook: {hook_opening}\\nKey Points: {key_points}\\nCTA: {cta_closing}\\n\\nPreview: {rendered_video_url}\\n\\nApprove to publish to LinkedIn?",
        },
      }),

      // 6. LinkedIn video publisher
      n("6", "hitApi", "Publish video to LinkedIn", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.linkedin.com/v2/ugcPosts",
          headers: [
            { key: "Authorization", value: "Bearer {linkedin_token}" },
            { key: "Content-Type", value: "application/json" },
            { key: "X-Restli-Protocol-Version", value: "2.0.0" },
          ],
          body: [
            { key: "author", value: "urn:li:person:{linkedin_person_id}" },
            { key: "lifecycleState", value: "PUBLISHED" },
            {
              key: "specificContent",
              value:
                '{"com.linkedin.ugc.ShareContent":{"shareCommentary":{"text":"{video_title}\\n\\n{key_points}\\n\\n{cta_closing}"},"shareMediaCategory":"VIDEO","media":[{"status":"READY","media":"{rendered_video_url}","title":{"text":"{video_title}"}}]}}',
            },
            {
              key: "visibility",
              value: '{"com.linkedin.ugc.MemberNetworkVisibility":"PUBLIC"}',
            },
          ],
          variables: [
            {
              name: "id",
              customName: "linkedin_video_post_id",
              type: "string",
            },
          ],
        },
      }),

      // 7. Video performance monitor (Sheets)
      sheetsNode(
        "7",
        "Log LinkedIn video performance to Sheets",
        1480,
        "LinkedInVideoPerformance",
        '[["{video_title}","{speaker_name}","{video_duration}s","{render_status}","{linkedin_video_post_id}","{key_points}","{hook_opening}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // passes quality -> approval
      e("4", "7", "output_2"), // fails quality -> log for review
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1043. LOCALIZED MARKET VIDEO AD (8 nodes)
  // ===================================================================

  "Localized Market Video Ad": {
    summary:
      "Triggers on a market expansion brief, researches local markets via SerpAPI, generates AI localized scripts, reviews for cultural sensitivity, renders localized video, gates through approval, publishes to regional platforms, and handles errors via Slack.",
    nodes: [
      trigger("Webhook: Localized video ad brief", "webhook_app", {
        source: "internal",
        event: "localized_ad.requested",
        howItStarts:
          "Fires when the marketing team requests a localized video ad for a specific market, including target region, language, cultural notes, and campaign objectives",
        setup:
          "Configure webhook from campaign planning tool. Set SerpAPI key for local market research. Provide Creatomate API key for video rendering. Configure regional platform credentials (Meta, YouTube, TikTok).",
        inputFields: [],
      }),

      // 2. Local market research (SerpAPI)
      aiNode(
        "2",
        "AI: Extract brief and research local market",
        280,
        "You are a localization strategist. Extract the market expansion brief from the webhook payload and identify key local market research queries. Return JSON.",
        "Extract localized ad brief: {webhook_payload}. Return target_region (e.g., Japan, Brazil, Germany), target_language, campaign_objective, product_name, brand_guidelines, cultural_notes, budget_range, target_platform (Meta, YouTube, TikTok), search_query (local market consumer behavior search).",
        [
          { field: "target_region", as: "target_region", type: "string" },
          { field: "target_language", as: "target_language", type: "string" },
          {
            field: "campaign_objective",
            as: "campaign_objective",
            type: "string",
          },
          { field: "product_name", as: "product_name", type: "string" },
          { field: "brand_guidelines", as: "brand_guidelines", type: "string" },
          { field: "cultural_notes", as: "cultural_notes", type: "string" },
          { field: "budget_range", as: "budget_range", type: "string" },
          { field: "target_platform", as: "target_platform", type: "string" },
          { field: "search_query", as: "market_search_query", type: "string" },
        ],
      ),

      // 3. AI localized script
      aiNode(
        "3",
        "AI: Write localized video ad script",
        520,
        "You are a multilingual video ad scriptwriter with deep cultural expertise. Write a video ad script that resonates with the target market's cultural values, humor, and communication style. The script should feel native, not translated. Return JSON.",
        "Write a localized video ad script. Region: {target_region}. Language: {target_language}. Product: {product_name}. Objective: {campaign_objective}. Cultural notes: {cultural_notes}. Brand: {brand_guidelines}. Platform: {target_platform}. Return script_native_language (full script in target language), script_english_translation (for internal review), cultural_hooks (local references used), local_cta (CTA in native language), duration_seconds (number), visual_direction (culturally appropriate visuals), music_style (locally popular genre), sensitivity_flags (potential cultural issues to review).",
        [
          {
            field: "script_native_language",
            as: "localized_script",
            type: "string",
          },
          {
            field: "script_english_translation",
            as: "english_translation",
            type: "string",
          },
          { field: "cultural_hooks", as: "cultural_hooks", type: "string" },
          { field: "local_cta", as: "local_cta", type: "string" },
          { field: "duration_seconds", as: "ad_duration", type: "number" },
          { field: "visual_direction", as: "visual_direction", type: "string" },
          { field: "music_style", as: "music_style", type: "string" },
          {
            field: "sensitivity_flags",
            as: "sensitivity_flags",
            type: "string",
          },
        ],
      ),

      // 4. Cultural sensitivity review condition
      n(
        "4",
        "condition",
        "Does the script pass cultural sensitivity review?",
        760,
        {
          conditionData: [
            {
              variable: "sensitivity_flags",
              value: "none",
              comparison_operator: "EQUAL TO",
              logical_operator: "OR",
            },
            {
              variable: "sensitivity_flags",
              value: "",
              comparison_operator: "EQUAL TO",
              logical_operator: "END",
            },
          ],
        },
      ),

      // 5. Localized video rendering (True path — passes review)
      n("5", "hitApi", "Render localized video ad via Creatomate", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.creatomate.com/v1/renders",
          headers: [
            { key: "Authorization", value: "Bearer {creatomate_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "template_id", value: "{creatomate_localized_template_id}" },
            {
              key: "modifications",
              value:
                '{"script":"{localized_script}","cta":"{local_cta}","region":"{target_region}","language":"{target_language}","visuals":"{visual_direction}","music":"{music_style}","cultural_hooks":"{cultural_hooks}"}',
            },
          ],
          variables: [
            { name: "id", customName: "render_id", type: "string" },
            { name: "status", customName: "render_status", type: "string" },
            { name: "url", customName: "localized_video_url", type: "string" },
          ],
        },
      }),

      // 6. Approval gate
      n("6", "approvalGate", "Approve localized video ad", 1240, {
        approvalGateData: {
          message:
            "Localized Video Ad Review\\n\\nRegion: {target_region}\\nLanguage: {target_language}\\nProduct: {product_name}\\nPlatform: {target_platform}\\n\\nScript (English):\\n{english_translation}\\n\\nCultural Hooks: {cultural_hooks}\\nLocal CTA: {local_cta}\\nDuration: {ad_duration}s\\n\\nSensitivity Flags: {sensitivity_flags}\\nVisual Direction: {visual_direction}\\n\\nPreview: {localized_video_url}\\n\\nApprove for regional publishing?",
        },
      }),

      // 7. Regional platform publisher
      n("7", "hitApi", "Publish localized ad to regional platform", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://graph.facebook.com/v18.0/act_{meta_ad_account_id}/adcreatives",
          headers: [
            { key: "Authorization", value: "Bearer {meta_access_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "name",
              value: "Localized Ad: {product_name} - {target_region}",
            },
            {
              key: "object_story_spec",
              value:
                '{"page_id":"{meta_page_id}","video_data":{"video_url":"{localized_video_url}","title":"{product_name}","message":"{localized_script}","call_to_action":{"type":"LEARN_MORE","value":{"link":"{company_url}/{target_language}"}}}}',
            },
          ],
          variables: [
            { name: "id", customName: "meta_creative_id", type: "string" },
          ],
        },
      }),

      // 8. Localization error handler (Slack — False path catches sensitivity issues)
      slackNode(
        "8",
        "Slack: Localization review alert",
        1720,
        "LOCALIZATION ALERT: {product_name} — {target_region}\\n\\nSensitivity Flags: {sensitivity_flags}\\n\\nScript (English): {english_translation}\\n\\nCultural Hooks: {cultural_hooks}\\nVisual Direction: {visual_direction}\\n\\nAction Required: Review flagged cultural sensitivity issues before proceeding with ad render.\\n\\nRender Status: {render_status}\\nPlatform: {target_platform}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // passes sensitivity -> render
      e("4", "8", "output_2"), // sensitivity flags -> alert team
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1044. LOW-ENGAGEMENT SUNSET FLOW (7 nodes)
  // ===================================================================

  "Low-Engagement Sunset Flow": {
    summary:
      "Detects engagement decline on a schedule, validates engagement history in code, sends an AI gentle re-engagement email, checks for engagement response with a condition, reduces email frequency for partial engagers, makes a final sunset decision, and logs outcomes to Google Sheets.",
    nodes: [
      trigger("Schedule: Engagement decline detection", "schedule", {
        schedule: "0 7 * * 3 (Wednesday at 7 AM)",
        howItStarts:
          "A weekly schedule scans email engagement metrics to identify subscribers showing declining open and click rates over the past 30-60 days, before they become fully unengaged",
        setup:
          "Configure your ESP API key (Klaviyo, SendGrid, or Mailchimp) with analytics read access. Set engagement decline thresholds (e.g., open rate dropped below 5%). Provide Google Sheets spreadsheet ID.",
        inputFields: [],
      }),

      // 2. Engagement history validation (runCode)
      n(
        "2",
        "runCode",
        "Validate engagement trends and identify declining subscribers",
        280,
        {
          runCodeDetails: {
            language: "javascript",
            code: `// Analyze engagement trends to identify declining subscribers
// Simulates fetching from ESP analytics API
const now = Date.now();
const thirtyDaysAgo = now - 30 * 86400000;
const sixtyDaysAgo = now - 60 * 86400000;
// In production, this would fetch from Klaviyo/SendGrid analytics API
const sampleSubscribers = [
  { email: "user1@example.com", name: "User One", opens_30d: 1, opens_60d: 8, clicks_30d: 0, clicks_60d: 3, last_open: new Date(now - 25 * 86400000).toISOString() },
  { email: "user2@example.com", name: "User Two", opens_30d: 0, opens_60d: 5, clicks_30d: 0, clicks_60d: 2, last_open: new Date(now - 45 * 86400000).toISOString() },
];
const declining = sampleSubscribers.filter(s => {
  const openDecline = s.opens_60d > 0 ? (1 - s.opens_30d / (s.opens_60d / 2)) : 0;
  return openDecline > 0.5 || s.opens_30d === 0;
});
return {
  total_scanned: sampleSubscribers.length,
  declining_count: declining.length,
  declining_profiles_json: JSON.stringify(declining),
  first_declining_email: declining.length > 0 ? declining[0].email : "",
  first_declining_name: declining.length > 0 ? declining[0].name : "",
  avg_open_decline: declining.length > 0 ? "significant" : "minimal",
  scan_date: new Date().toISOString()
};`,
            outputVariables: [
              { name: "total_scanned", type: "number" },
              { name: "declining_count", type: "number" },
              { name: "declining_profiles_json", type: "string" },
              { name: "first_declining_email", type: "string" },
              { name: "first_declining_name", type: "string" },
              { name: "avg_open_decline", type: "string" },
              { name: "scan_date", type: "string" },
            ],
          },
        },
      ),

      // 3. AI gentle re-engagement email
      aiNode(
        "3",
        "AI: Generate gentle re-engagement email",
        520,
        "You are a subscriber retention specialist. Write a gentle, non-aggressive re-engagement email that acknowledges the subscriber may be busy, offers value, and gives them easy options to adjust their preferences rather than unsubscribe. Return JSON.",
        "Write a gentle re-engagement email for {first_declining_name} ({first_declining_email}) showing declining engagement. Company: {company_name}. Return subject (warm, not guilt-tripping), body_html (acknowledge busy lives, highlight best recent content, offer frequency options: weekly digest, monthly highlights, or pause), preference_link_text (CTA for preference center), plain_text.",
        [
          { field: "subject", as: "reengage_subject", type: "string" },
          { field: "body_html", as: "reengage_body", type: "string" },
          {
            field: "preference_link_text",
            as: "preference_cta",
            type: "string",
          },
          { field: "plain_text", as: "reengage_plain", type: "string" },
        ],
      ),

      // 4. Engagement check condition
      n("4", "condition", "Did subscriber show any engagement signal?", 760, {
        conditionData: [
          {
            variable: "declining_count",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "END",
          },
        ],
      }),

      // 5. Frequency reduction (True path — some engagement)
      n("5", "runCode", "Reduce email frequency for partially engaged", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Prepare frequency reduction actions for declining but not dead subscribers
const declining = JSON.parse(vars.declining_profiles_json || "[]");
const frequencyActions = declining.map(s => {
  const hasRecentOpen = s.opens_30d > 0;
  return {
    email: s.email,
    action: hasRecentOpen ? "reduce_to_weekly" : "reduce_to_monthly",
    current_frequency: "daily",
    new_frequency: hasRecentOpen ? "weekly_digest" : "monthly_highlights",
    reason: hasRecentOpen ? "Low but present engagement" : "No opens in 30 days"
  };
});
const weeklyCount = frequencyActions.filter(a => a.action === "reduce_to_weekly").length;
const monthlyCount = frequencyActions.filter(a => a.action === "reduce_to_monthly").length;
return {
  frequency_actions_json: JSON.stringify(frequencyActions),
  reduced_to_weekly: weeklyCount,
  reduced_to_monthly: monthlyCount,
  frequency_summary: weeklyCount + " to weekly, " + monthlyCount + " to monthly"
};`,
          outputVariables: [
            { name: "frequency_actions_json", type: "string" },
            { name: "reduced_to_weekly", type: "number" },
            { name: "reduced_to_monthly", type: "number" },
            { name: "frequency_summary", type: "string" },
          ],
        },
      }),

      // 6. Final sunset decision (runCode)
      n("6", "runCode", "Make final sunset decisions", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Final sunset decision: suppress subscribers who show zero signals
const declining = JSON.parse(vars.declining_profiles_json || "[]");
const toSunset = declining.filter(s => s.opens_30d === 0 && s.clicks_30d === 0);
const toKeep = declining.filter(s => s.opens_30d > 0 || s.clicks_30d > 0);
return {
  sunset_count: toSunset.length,
  keep_count: toKeep.length,
  sunset_emails: JSON.stringify(toSunset.map(s => s.email)),
  sunset_action: toSunset.length > 0 ? "suppress_from_campaigns" : "no_action",
  decision_summary: toSunset.length + " sunset, " + toKeep.length + " keep (reduced frequency)"
};`,
          outputVariables: [
            { name: "sunset_count", type: "number" },
            { name: "keep_count", type: "number" },
            { name: "sunset_emails", type: "string" },
            { name: "sunset_action", type: "string" },
            { name: "decision_summary", type: "string" },
          ],
        },
      }),

      // 7. Sunset executor + logger (Sheets)
      sheetsNode(
        "7",
        "Log sunset flow outcomes to Sheets",
        1480,
        "SunsetFlowLog",
        '[["{scan_date}","{total_scanned}","{declining_count}","{frequency_summary}","{sunset_count}","{keep_count}","{sunset_action}","{decision_summary}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // has declining subscribers -> reduce frequency
      e("4", "7", "output_2"), // no declining subscribers -> log only
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1045. LOYALTY INACTIVE WINBACK (7 nodes)
  // ===================================================================

  "Loyalty Inactive Winback": {
    summary:
      "Scans for inactive loyalty members on a schedule, enriches with points balance and tier data from HubSpot, generates an AI loyalty re-engagement email, dispatches it, checks bonus points incentive eligibility with a condition, monitors engagement and redemption, and logs results to Google Sheets.",
    nodes: [
      trigger("Schedule: Inactive loyalty member scan", "schedule", {
        schedule: "0 8 * * 4 (Thursday at 8 AM)",
        howItStarts:
          "A weekly schedule scans the loyalty program for members who have not earned or redeemed points in the past 60 days, triggering personalized win-back outreach",
        setup:
          "Configure HubSpot API key with contacts and custom objects read access for loyalty data. Set SendGrid API key for email dispatch. Define inactivity threshold (e.g., 60 days). Provide Google Sheets spreadsheet ID.",
        inputFields: [],
      }),

      // 2. Points balance + tier enrichment (HubSpot)
      hubspotGetNode(
        "2",
        "Fetch inactive loyalty member data from HubSpot",
        280,
        "crm/v3/objects/contacts?properties=email,firstname,lastname,loyalty_tier,loyalty_points_balance,last_points_earned_date,last_redemption_date,total_lifetime_points&limit=100&archived=false",
        [
          {
            name: "results",
            customName: "loyalty_members_json",
            type: "string",
          },
          {
            name: "total",
            customName: "total_members_fetched",
            type: "number",
          },
        ],
      ),

      // 3. AI loyalty re-engagement email
      aiNode(
        "3",
        "AI: Generate loyalty re-engagement email",
        520,
        "You are a loyalty program email specialist. Write a personalized re-engagement email that reminds members of their unused points balance, tier benefits, and exclusive rewards they are missing. Create a sense of value, not guilt. Return JSON.",
        "Write a loyalty re-engagement email. Company: {company_name}. Member data available: {loyalty_members_json}. Create a template that uses points_balance, tier_name, and member_name dynamically. Return subject (points-focused, curiosity-driven), body_html (personalized points reminder, tier perks they are missing, easy redemption suggestions, bonus points offer), bonus_offer_text (e.g., Earn 2x points this week), plain_text.",
        [
          { field: "subject", as: "loyalty_subject", type: "string" },
          { field: "body_html", as: "loyalty_body", type: "string" },
          { field: "bonus_offer_text", as: "bonus_offer", type: "string" },
          { field: "plain_text", as: "loyalty_plain", type: "string" },
        ],
      ),

      // 4. Email dispatch
      emailNode(
        "4",
        "Send loyalty re-engagement email",
        760,
        "{member_email}",
        "{loyalty_subject}",
        "{loyalty_body}",
      ),

      // 5. Bonus points incentive condition
      n(
        "5",
        "condition",
        "Is member eligible for bonus points incentive?",
        1000,
        {
          conditionData: [
            {
              variable: "total_members_fetched",
              value: "0",
              comparison_operator: "GREATER THAN",
              logical_operator: "END",
            },
          ],
        },
      ),

      // 6. Engagement + redemption monitor
      n("6", "runCode", "Monitor engagement and prepare tier alerts", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Analyze loyalty member data and prepare engagement monitoring
const members = JSON.parse(vars.loyalty_members_json || "[]");
const now = Date.now();
const sixtyDaysAgo = now - 60 * 86400000;
const inactive = members.filter(m => {
  const lastEarned = m.properties?.last_points_earned_date ? new Date(m.properties.last_points_earned_date).getTime() : 0;
  const lastRedeemed = m.properties?.last_redemption_date ? new Date(m.properties.last_redemption_date).getTime() : 0;
  return Math.max(lastEarned, lastRedeemed) < sixtyDaysAgo;
});
const tierBreakdown = {};
inactive.forEach(m => {
  const tier = m.properties?.loyalty_tier || "Unknown";
  tierBreakdown[tier] = (tierBreakdown[tier] || 0) + 1;
});
const totalUnusedPoints = inactive.reduce((sum, m) => sum + (parseInt(m.properties?.loyalty_points_balance) || 0), 0);
const atRiskHighTier = inactive.filter(m => ["Gold", "Platinum", "VIP"].includes(m.properties?.loyalty_tier));
return {
  inactive_count: inactive.length,
  tier_breakdown: JSON.stringify(tierBreakdown),
  total_unused_points: totalUnusedPoints,
  high_tier_at_risk: atRiskHighTier.length,
  high_tier_emails: JSON.stringify(atRiskHighTier.map(m => m.properties?.email)),
  engagement_summary: inactive.length + " inactive members, " + totalUnusedPoints + " unused points, " + atRiskHighTier.length + " high-tier at risk"
};`,
          outputVariables: [
            { name: "inactive_count", type: "number" },
            { name: "tier_breakdown", type: "string" },
            { name: "total_unused_points", type: "number" },
            { name: "high_tier_at_risk", type: "number" },
            { name: "high_tier_emails", type: "string" },
            { name: "engagement_summary", type: "string" },
          ],
        },
      }),

      // 7. Results logger + tier alert (Sheets)
      sheetsNode(
        "7",
        "Log loyalty winback results to Sheets",
        1480,
        "LoyaltyWinback",
        '[["{scan_date}","{total_members_fetched}","{inactive_count}","{total_unused_points}","{high_tier_at_risk}","{tier_breakdown}","{loyalty_subject}","{bonus_offer}","{engagement_summary}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // eligible for bonus -> monitor
      e("5", "7", "output_2"), // not eligible -> log only
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1046. LOYALTY JOIN CAPTURE (6 nodes)
  // ===================================================================

  "Loyalty Join Capture": {
    summary:
      "Triggers on a loyalty signup event, captures email and SMS contact details, validates data integrity in code, creates the loyalty account, sends a dual-channel welcome via email and SMS, and handles errors and duplicates via Slack.",
    nodes: [
      trigger("Webhook: Loyalty program signup", "webhook_app", {
        source: "Loyalty Platform / Website",
        event: "loyalty.signup",
        howItStarts:
          "Fires when a customer signs up for the loyalty program through the website, mobile app, or in-store POS, providing their contact details and consent preferences",
        setup:
          "Configure webhook from your loyalty platform or signup form. Set HubSpot API key for account creation. Provide SendGrid and Twilio credentials for dual-channel welcome. Set Slack webhook for error handling.",
        inputFields: [],
      }),

      // 2. Email + SMS capture handler
      aiNode(
        "2",
        "AI: Extract and validate signup contact details",
        280,
        "You are a data validation assistant for loyalty program signups. Extract all contact details from the webhook payload regardless of format. Validate email format and phone number. Return JSON.",
        "Extract loyalty signup details from: {webhook_payload}. Return email (validated email), phone (E.164 format), first_name, last_name, signup_source (web, app, pos), email_consent (true/false), sms_consent (true/false), referral_code (if any), preferred_language (default en-US).",
        [
          { field: "email", as: "member_email", type: "string" },
          { field: "phone", as: "member_phone", type: "string" },
          { field: "first_name", as: "member_first_name", type: "string" },
          { field: "last_name", as: "member_last_name", type: "string" },
          { field: "signup_source", as: "signup_source", type: "string" },
          { field: "email_consent", as: "email_consent", type: "string" },
          { field: "sms_consent", as: "sms_consent", type: "string" },
          { field: "referral_code", as: "referral_code", type: "string" },
          {
            field: "preferred_language",
            as: "preferred_language",
            type: "string",
          },
        ],
      ),

      // 3. Data validation (runCode)
      n("3", "runCode", "Validate signup data and check for duplicates", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Validate loyalty signup data integrity
const email = vars.member_email || "";
const phone = vars.member_phone || "";
const firstName = vars.member_first_name || "";
const lastName = vars.member_last_name || "";
const emailValid = /^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$/.test(email);
const phoneValid = /^\\+[1-9]\\d{6,14}$/.test(phone);
const issues = [];
if (!emailValid) issues.push("Invalid email: " + email);
if (!phoneValid && vars.sms_consent === "true") issues.push("Invalid phone for SMS: " + phone);
if (!firstName) issues.push("Missing first name");
const membershipId = "LYL-" + Date.now().toString(36).toUpperCase() + "-" + Math.random().toString(36).substring(2, 6).toUpperCase();
return {
  data_valid: issues.length === 0 ? "yes" : "no",
  membership_id: membershipId,
  email_valid: emailValid ? "yes" : "no",
  phone_valid: phoneValid ? "yes" : "no",
  validation_issues: issues.join("; ") || "All validations passed",
  starting_points: "100",
  starting_tier: "Bronze",
  signup_timestamp: new Date().toISOString()
};`,
          outputVariables: [
            { name: "data_valid", type: "string" },
            { name: "membership_id", type: "string" },
            { name: "email_valid", type: "string" },
            { name: "phone_valid", type: "string" },
            { name: "validation_issues", type: "string" },
            { name: "starting_points", type: "string" },
            { name: "starting_tier", type: "string" },
            { name: "signup_timestamp", type: "string" },
          ],
        },
      }),

      // 4. Loyalty account creation (HubSpot)
      n("4", "hitApi", "Create loyalty account in HubSpot", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/contacts",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "properties",
              value:
                '{"email":"{member_email}","firstname":"{member_first_name}","lastname":"{member_last_name}","phone":"{member_phone}","loyalty_member_id":"{membership_id}","loyalty_tier":"{starting_tier}","loyalty_points_balance":"{starting_points}","loyalty_signup_date":"{signup_timestamp}","loyalty_signup_source":"{signup_source}","preferred_language":"{preferred_language}"}',
            },
          ],
          variables: [
            { name: "id", customName: "hubspot_contact_id", type: "string" },
            {
              name: "properties.email",
              customName: "created_email",
              type: "string",
            },
          ],
        },
      }),

      // 5. Dual-channel welcome (email + SMS)
      emailNode(
        "5",
        "Send loyalty welcome email and SMS",
        1000,
        "{member_email}",
        "Welcome to {company_name} Rewards! Your membership is active.",
        "Hi {member_first_name},\\n\\nWelcome to {company_name} Rewards!\\n\\nYour membership details:\\n- Member ID: {membership_id}\\n- Starting Tier: {starting_tier}\\n- Welcome Bonus: {starting_points} points\\n\\nStart earning points on every purchase. Here is how:\\n1. Shop online or in-store\\n2. Refer friends for bonus points\\n3. Engage with our content\\n\\nYour points never expire as long as your account is active.\\n\\nHappy shopping!\\n{company_name} Rewards Team",
      ),

      // 6. Error + duplicate handler (Slack)
      slackNode(
        "6",
        "Slack: Loyalty signup error/duplicate handler",
        1240,
        "Loyalty Signup Processed\\n\\nMember: {member_first_name} {member_last_name}\\nEmail: {member_email}\\nPhone: {member_phone}\\nMembership ID: {membership_id}\\nTier: {starting_tier}\\nPoints: {starting_points}\\nSource: {signup_source}\\n\\nValidation: {validation_issues}\\nHubSpot Contact: {hubspot_contact_id}\\nReferral Code: {referral_code}",
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5"), e("5", "6")],
  },

  // ===================================================================
  // 1047. LOYALTY JOIN WELCOME (8 nodes)
  // ===================================================================

  "Loyalty Join Welcome": {
    summary:
      "Triggers on loyalty enrollment, enriches the member profile from HubSpot, sends a 3-email AI welcome sequence (overview, quick win guide, redemption inspiration) with delays, checks for first earn celebration with a condition, monitors engagement, and logs onboarding outcomes to Google Sheets.",
    nodes: [
      trigger("Webhook: Loyalty enrollment confirmed", "webhook_app", {
        source: "Loyalty Platform",
        event: "loyalty.enrollment_confirmed",
        howItStarts:
          "Fires when a new loyalty member's enrollment is confirmed and their account is fully activated, triggering the multi-touch welcome onboarding sequence",
        setup:
          "Configure webhook from your loyalty platform for enrollment confirmation events. Set HubSpot API key for profile enrichment. Provide SendGrid API key for email sequence. Set Google Sheets spreadsheet ID.",
        inputFields: [],
      }),

      // 2. Member profile enrichment (HubSpot)
      aiNode(
        "2",
        "AI: Extract enrollment data and enrich profile",
        280,
        "You are a loyalty program data specialist. Extract the new member enrollment details from the webhook payload regardless of format. Return JSON.",
        "Extract loyalty enrollment details: {webhook_payload}. Return member_email, member_name, membership_id, loyalty_tier, points_balance (number), signup_source, preferred_categories (interests or empty), referral_source (how they heard about the program).",
        [
          { field: "member_email", as: "member_email", type: "string" },
          { field: "member_name", as: "member_name", type: "string" },
          { field: "membership_id", as: "membership_id", type: "string" },
          { field: "loyalty_tier", as: "loyalty_tier", type: "string" },
          { field: "points_balance", as: "points_balance", type: "number" },
          { field: "signup_source", as: "signup_source", type: "string" },
          {
            field: "preferred_categories",
            as: "preferred_categories",
            type: "string",
          },
          { field: "referral_source", as: "referral_source", type: "string" },
        ],
      ),

      // 3. AI welcome email #1 overview
      aiNode(
        "3",
        "AI: Welcome email #1 — Program Overview",
        520,
        "You are a loyalty program welcome email specialist. Write the first email in a 3-part welcome series that introduces the program, explains how it works, and excites the member about the benefits. Return JSON.",
        "Write loyalty welcome email #1 (program overview) for {member_name}. Membership ID: {membership_id}. Tier: {loyalty_tier}. Points: {points_balance}. Company: {company_name}. Return subject (exciting welcome), body_html (program intro, tier benefits, how points work, first action CTA), key_benefit_highlighted, plain_text.",
        [
          { field: "subject", as: "welcome1_subject", type: "string" },
          { field: "body_html", as: "welcome1_body", type: "string" },
          {
            field: "key_benefit_highlighted",
            as: "welcome1_benefit",
            type: "string",
          },
          { field: "plain_text", as: "welcome1_plain", type: "string" },
        ],
      ),

      // 4. AI welcome email #2 quick win guide (delay)
      n("4", "delayAction", "Wait 3 days before quick win guide", 760, {
        delayDetails: { duration: 3, unit: "days" },
      }),

      // 5. AI welcome email #3 redemption inspiration (delay)
      aiNode(
        "5",
        "AI: Welcome emails #2 Quick Win + #3 Redemption",
        1000,
        "You are a loyalty program engagement specialist. Write TWO emails: (1) Quick Win Guide showing the easiest ways to earn their first points, and (2) Redemption Inspiration showcasing the best rewards they can redeem. Return JSON.",
        "Write loyalty welcome emails #2 and #3 for {member_name}. Tier: {loyalty_tier}. Points: {points_balance}. Interests: {preferred_categories}. Company: {company_name}. Email #2: Quick Win Guide (3 easy ways to earn points fast). Email #3: Redemption Inspiration (aspirational rewards at each tier). Return email2_subject, email2_body_html (step-by-step earning guide), email3_subject, email3_body_html (visual reward showcase), combined_plain_text.",
        [
          { field: "email2_subject", as: "welcome2_subject", type: "string" },
          { field: "email2_body_html", as: "welcome2_body", type: "string" },
          { field: "email3_subject", as: "welcome3_subject", type: "string" },
          { field: "email3_body_html", as: "welcome3_body", type: "string" },
          {
            field: "combined_plain_text",
            as: "welcome23_plain",
            type: "string",
          },
        ],
      ),

      // 6. First earn celebration condition
      n("6", "condition", "Has member earned their first points?", 1240, {
        conditionData: [
          {
            variable: "points_balance",
            value: "100",
            comparison_operator: "GREATER THAN",
            logical_operator: "END",
          },
        ],
      }),

      // 7. Engagement check (True path — earned points)
      aiNode(
        "7",
        "AI: First earn celebration and next milestone email",
        1480,
        "You are a loyalty program celebration copywriter. Write an enthusiastic celebration email for a member who just earned their first points. Include their progress toward the next reward and the next milestone to aim for. Return JSON.",
        "Write a first-earn celebration email for {member_name}. Points: {points_balance}. Tier: {loyalty_tier}. Interests: {preferred_categories}. Company: {company_name}. Return subject (celebratory), body_html (congratulations, points earned visual, next reward preview, next milestone), next_milestone (what to aim for), plain_text.",
        [
          { field: "subject", as: "celebrate_subject", type: "string" },
          { field: "body_html", as: "celebrate_body", type: "string" },
          { field: "next_milestone", as: "next_milestone", type: "string" },
          { field: "plain_text", as: "celebrate_plain", type: "string" },
        ],
      ),

      // 8. Onboarding outcome logger (Sheets)
      sheetsNode(
        "8",
        "Log loyalty onboarding outcome to Sheets",
        1720,
        "LoyaltyOnboarding",
        '[["{member_email}","{member_name}","{membership_id}","{loyalty_tier}","{points_balance}","{signup_source}","{welcome1_subject}","{welcome2_subject}","{welcome3_subject}","{next_milestone}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // earned first points -> celebrate
      e("6", "8", "output_2"), // not yet earned -> log onboarding status
      e("7", "8"),
    ],
  },
};
